import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Header from '../Layout/Header';
import Footer from '../Layout/Footer';
import FloatingChatSystem from '../FloatingChatSystem';
import { 
  User, 
  FileText, 
  DollarSign, 
  Briefcase, 
  MessageSquare, 
  Calendar,
  TrendingUp,
  Bell,
  Settings,
  LogOut,
  ChevronRight,
  Activity,
  Clock,
  CheckCircle,
  AlertCircle,
  CreditCard,
  Receipt,
  Folder,
  BarChart3,
  Download,
  Eye,
  Edit,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  ChevronUp,
  ChevronDown,
  Mail,
  Phone,
  Printer,
  X,
  Package,
  Monitor,
  Star,
  RefreshCw,
  Tag,
  Save
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useApiCall } from '../../hooks/useApi';
import { projectAPI, quoteAPI, contactAPI, orderAPI, invoiceAPI, emiAPI, fileAPI, analyticsAPI, appointmentAPI, pdfGenerationAPI, paymentTransactionsAPI, paymentOptionsAPI } from '../../services/api';
import { getImageUrl } from '../../utils/apiUtils';
import { getPaymentTypeWithInstallment } from '../../utils/installmentUtils';
import { mapPaymentMethodToDisplay, mapPaymentStatusToDisplay, mapOrderStatusToDisplay, getStatusColor } from '../../utils/paymentUtils';
import MyConsole from './MyConsole';
import UserCoupons from './UserCoupons';

const UserDashboard = () => {
  const { user, logout, updateProfile } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Success message state
  const [successMessage, setSuccessMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [activeSection, setActiveSection] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  
  // Project creation modal state
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectError, setProjectError] = useState(null);

  // Quote creation modal state
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);

  // Appointment booking modal state
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [appointmentLoading, setAppointmentLoading] = useState(false);
  const [appointmentError, setAppointmentError] = useState(null);


  
  // EMI/Split Payments modal state
  const [showEMISplitPaymentModal, setShowEMISplitPaymentModal] = useState(false);
  const [selectedEMISplitPayment, setSelectedEMISplitPayment] = useState(null);

  // Invoice details modal state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Quote details modal state
  const [showQuoteDetailsModal, setShowQuoteDetailsModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);

  // Appointment details modal state
  const [showAppointmentDetailsModal, setShowAppointmentDetailsModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  // Profile state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    whatsapp: user?.whatsapp || ''
  });
  const [profileUpdateSuccess, setProfileUpdateSuccess] = useState(false);
  const [profileUpdateError, setProfileUpdateError] = useState('');



  // State for EMI/Split payments
  const [emiSplitPayments, setEmiSplitPayments] = useState([]);
  const [emiSplitLoading, setEmiSplitLoading] = useState(true);
  const [emiSplitError, setEmiSplitError] = useState(null);

  // State for payment options settings
  const [paymentOptionsSettings, setPaymentOptionsSettings] = useState(null);
  const [paymentOptionsLoading, setPaymentOptionsLoading] = useState(false);

  // State for current payment options configuration
  const [currentPaymentConfig, setCurrentPaymentConfig] = useState(null);
  const [paymentConfigLoading, setPaymentConfigLoading] = useState(false);

  // State for split payment orders (remaining amounts)
  const [splitPaymentOrders, setSplitPaymentOrders] = useState([]);
  const [splitPaymentOrdersLoading, setSplitPaymentOrdersLoading] = useState(false);

  // Update profileData when user data changes
  useEffect(() => {
    const updatedData = {
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      address: user?.address || '',
      whatsapp: user?.whatsapp || ''
    };
    setProfileData(updatedData);
  }, [user]);



  // Handle location state for success messages and refresh data
  useEffect(() => {
    if (location.state?.message && location.state?.type === 'success') {
      setSuccessMessage(location.state.message);
      // Clear the location state to prevent showing the message again
      navigate(location.pathname, { replace: true });
      
      // Refresh EMI/Split payments data to show updated status
      if (fetchEMISplitPayments) {
        fetchEMISplitPayments();
      }
    }
  }, [location.state, navigate, location.pathname]);

  // Fetch user's data
  const { data: projectsResponse, loading: projectsLoading, refetch: refetchProjects } = useApiCall(
    () => projectAPI.getMyProjects({ limit: 10 }),
    [],
    true
  );

  // Extract projects array from response
  const projects = projectsResponse?.data || projectsResponse || [];

  const { data: quotesResponse, loading: quotesLoading, refetch: refetchQuotes } = useApiCall(
    () => quoteAPI.getMyQuotes({ limit: 10 }),
    [],
    true
  );

  // Extract quotes array from response
  const quotes = quotesResponse?.data || quotesResponse || [];

  const { data: statsResponse, loading: statsLoading } = useApiCall(
    () => projectAPI.getStats(),
    [],
    true
  );

  // Extract stats from response
  const stats = statsResponse?.data || statsResponse || {};

  // Fetch additional dashboard data
  const { data: ordersResponse, loading: ordersLoading, error: ordersError } = useApiCall(
    () => orderAPI.getMyOrders({ limit: 10 }),
    [],
    true
  );
  const orders = ordersResponse?.data || ordersResponse || [];

  // Fetch current payment options configuration
  const fetchCurrentPaymentConfig = async () => {
    try {
      setPaymentConfigLoading(true);
      const response = await paymentOptionsAPI.getConfig();
      
      if (response?.data?.success) {
        const configData = {};
        Object.keys(response.data.data).forEach(key => {
          configData[key] = response.data.data[key].value;
        });
        setCurrentPaymentConfig(configData);
      }
    } catch (error) {
      console.error('Error fetching payment options config:', error);
    } finally {
      setPaymentConfigLoading(false);
    }
  };

  // Fetch payment options configuration on component mount
  useEffect(() => {
    fetchCurrentPaymentConfig();
  }, []);



  const { data: invoicesResponse, loading: invoicesLoading } = useApiCall(
    () => invoiceAPI.getMyInvoices({ limit: 10 }),
    [],
    true
  );
  const invoices = invoicesResponse?.data || invoicesResponse || [];

  const { data: emisResponse, loading: emisLoading } = useApiCall(
    () => emiAPI.getMyEMIs({ limit: 10 }),
    [],
    true
  );
  const emis = emisResponse?.data || emisResponse || [];

  const { data: appointmentsResponse, loading: appointmentsLoading, refetch: refetchAppointments } = useApiCall(
    () => appointmentAPI.getMyAppointments({ limit: 10 }),
    [],
    true
  );
  const appointments = appointmentsResponse?.data || appointmentsResponse || [];
  
  // Debug appointments data
  useEffect(() => {
    console.log('🔍 Appointments Debug:');
    console.log('Appointments Response:', appointmentsResponse);
    console.log('Appointments Data:', appointments);
    console.log('Appointments Loading:', appointmentsLoading);
  }, [appointmentsResponse, appointments, appointmentsLoading]);

  const { data: filesResponse, loading: filesLoading } = useApiCall(
    () => fileAPI.getMyFiles({ limit: 10 }),
    [],
    true
  );
  const files = filesResponse?.data || filesResponse || [];

  const { data: analyticsResponse, loading: analyticsLoading } = useApiCall(
    () => analyticsAPI.getMyAnalytics(),
    [],
    true
  );
  const analytics = analyticsResponse?.data || analyticsResponse || {};

  // Debug logging for API responses
  useEffect(() => {
    console.log('🔍 API Responses Debug:');
    console.log('Projects Response:', projectsResponse);
    console.log('Projects Data:', projects);
    console.log('Projects Loading:', projectsLoading);
    console.log('Orders Response:', ordersResponse);
    console.log('Orders Data:', orders);
    console.log('Orders Loading:', ordersLoading);
    console.log('Orders Error:', ordersError);

    console.log('Invoices:', invoicesResponse);
    console.log('Quotes:', quotesResponse);
    console.log('Appointments:', appointmentsResponse);
  }, [projectsResponse, projects, projectsLoading, ordersResponse, orders, ordersLoading, ordersError, invoicesResponse, quotesResponse, appointmentsResponse]);

  // Fetch EMI/Split payments function
  const fetchEMISplitPayments = async () => {
    try {
      setEmiSplitLoading(true);
      console.log('🔍 Fetching EMI/Split payments...');
      const response = await paymentTransactionsAPI.getUserTransactions({
        transaction_type: 'emi_payment,split_payment'
      });
      console.log('📊 EMI/Split payments response:', response);
      console.log('📊 EMI/Split payments data:', response.data.data);
      
      // Debug: Log each payment with its effective statuses
      if (response.data.data && response.data.data.length > 0) {
        console.log('🔍 EMI Payments with Effective Statuses:');
        response.data.data.forEach((payment, index) => {
          const isAdvancePayment = parseFloat(payment.amount || 0) === parseFloat(payment.order_total || 0);
          console.log(`${index + 1}. EMI Payment #${payment.id}:`, {
            order_id: payment.order_id,
            order_number: payment.order_number,
            installment_number: payment.installment_number,
            amount: payment.amount,
            order_total: payment.order_total,
            payment_status: payment.payment_status,
            installment_status: payment.installment_status,
            effectivePaymentStatus: payment.effectivePaymentStatus,
            effectiveInstallmentStatus: payment.effectiveInstallmentStatus,
            has_installment_order: payment.has_installment_order,
            installment_order_payment_status: payment.installment_order_payment_status,
            isAdvancePayment: isAdvancePayment,
            wouldBeCounted: !isAdvancePayment && 
                           (payment.effectiveInstallmentStatus || payment.installment_status) === 'completed' && 
                           (payment.effectivePaymentStatus || payment.payment_status) === 'completed' &&
                           payment.installment_number && 
                           payment.installment_number > 1 // Only actual installments, not advance
          });
        });
      }
      
      setEmiSplitPayments(response.data.data || []);
    } catch (err) {
      console.error('❌ Error fetching EMI/Split payments:', err);
      setEmiSplitError(err.message);
    } finally {
      setEmiSplitLoading(false);
    }
  };

  // Fetch EMI/Split payments on component mount
  useEffect(() => {
    fetchEMISplitPayments();
  }, []);

  // Refresh EMI/Split payments when returning from payment
  useEffect(() => {
    const handleFocus = () => {
      // Refresh data when user returns to the page (e.g., after payment)
      fetchEMISplitPayments();
      fetchSplitPaymentOrders();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Fetch payment options settings
  const fetchPaymentOptionsSettings = async () => {
    try {
      setPaymentOptionsLoading(true);
      const response = await paymentOptionsAPI.getConfig();
      console.log('🔍 Payment options settings:', response.data);
      setPaymentOptionsSettings(response.data.data || response.data);
    } catch (err) {
      console.error('❌ Error fetching payment options settings:', err);
    } finally {
      setPaymentOptionsLoading(false);
    }
  };

  // Fetch split payment orders (remaining amounts)
  const fetchSplitPaymentOrders = async () => {
    try {
      setSplitPaymentOrdersLoading(true);
      const response = await orderAPI.getMyOrders({ 
        limit: 100 
      });
      console.log('🔍 All orders for split payment matching:', response.data);
      
      // Filter orders that might be remaining split payments
      const allOrders = response.data.data || response.data || [];
      const splitPaymentOrders = allOrders.filter(order => 
        // Check for orders created for split payments
        (order.payment_option_details?.is_split_payment_order === true) ||
        (order.payment_option === 'split_payment') ||
        order.order_number.includes('After Demo') || 
        order.order_number.includes('After Project') ||
        order.notes?.includes('After Demo Payment') ||
        order.notes?.includes('After Project Payment')
      );
      
      console.log('🔍 Filtered split payment orders:', splitPaymentOrders);
      setSplitPaymentOrders(splitPaymentOrders);
    } catch (err) {
      console.error('❌ Error fetching split payment orders:', err);
    } finally {
      setSplitPaymentOrdersLoading(false);
    }
  };

  // Fetch payment options settings and split payment orders on component mount
  useEffect(() => {
    fetchPaymentOptionsSettings();
    fetchSplitPaymentOrders();
  }, []);

  // Calculate late fee for a given payment date and expiry date
  const calculateLateFee = (paymentDate, expiryDate, lateFeePerDay) => {
    if (!paymentDate || !expiryDate || !lateFeePerDay) {
      return { lateFee: 0, daysLate: 0, isLate: false };
    }

    const now = new Date();
    const expiry = new Date(expiryDate);
    
    // If current date is before expiry date, no late fee
    if (now <= expiry) {
      return { lateFee: 0, daysLate: 0, isLate: false };
    }

    // Calculate days late (rounded up to nearest day)
    const timeDiff = now.getTime() - expiry.getTime();
    const daysLate = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    // Calculate late fee
    const lateFee = daysLate * lateFeePerDay;
    
    return { lateFee, daysLate, isLate: true };
  };
  // Get remaining split payment amounts and their status
  const getRemainingSplitPaymentAmounts = (splitPayment) => {
    if (!splitPayment || splitPayment.transaction_type !== 'split_payment') {
      return null;
    }



    // If splitPaymentOrders is still loading, return a basic structure
    if (splitPaymentOrdersLoading) {
      return {
        advanceAmount: 0,
        afterDemoAmount: 0,
        afterProjectAmount: 0,
        afterDemoText: currentPaymentConfig?.advance_payment_after_demo_text || 'After Demo Completion',
        afterProjectText: currentPaymentConfig?.advance_payment_after_project_text || 'After Project Completion',
        isAdvanceCompleted: true, // Assume completed for now
        afterDemoOrder: null,
        afterProjectOrder: null,
        totalAmount: parseFloat(splitPayment.order_total || splitPayment.amount || 0) || 0,
        // Add payment dates and fine info
        afterDemoPaymentDate: '',
        afterProjectPaymentDate: '',
        afterDemoExpiryDate: '',
        afterProjectExpiryDate: '',
        lateFeePerDay: 500,
        // Add late fee details with default values
        demoLateFee: { lateFee: 0, daysLate: 0, isLate: false },
        projectLateFee: { lateFee: 0, daysLate: 0, isLate: false },
        // Original amounts without late fees
        originalAfterDemoAmount: 0,
        originalAfterProjectAmount: 0
      };
    }

    const details = splitPayment.payment_option_details;
    
    // Use current payment configuration instead of stored configuration
    let cfg = currentPaymentConfig || {};
    
    // Calculate the correct total amount for split payments
    let totalAmount = 0;
    if (splitPayment.transaction_type === 'split_payment') {
      // First try to get from original_total_amount field
      if (splitPayment.original_total_amount) {
        totalAmount = parseFloat(splitPayment.original_total_amount);
      }
      // If we have order_id, try to find the related order
      else if (splitPayment.order_id && orders.length > 0) {
        const relatedOrder = orders.find(order => order.id === splitPayment.order_id);
        if (relatedOrder) {
          // First check for original_total_amount (this is the correct total)
          if (relatedOrder.original_total_amount) {
            totalAmount = parseFloat(relatedOrder.original_total_amount);
          }
          // Fallback to total_amount
          else if (relatedOrder.total_amount) {
            totalAmount = parseFloat(relatedOrder.total_amount);
          }
        }
      }
      // Try to find by order_number as fallback
      else if (splitPayment.order_number && orders.length > 0) {
        const relatedOrderByNumber = orders.find(order => order.order_number === splitPayment.order_number);
        if (relatedOrderByNumber) {
          // First check for original_total_amount (this is the correct total)
          if (relatedOrderByNumber.original_total_amount) {
            totalAmount = parseFloat(relatedOrderByNumber.original_total_amount);
          }
          // Fallback to total_amount
          else if (relatedOrderByNumber.total_amount) {
            totalAmount = parseFloat(relatedOrderByNumber.total_amount);
          }
        }
      }
      // Last resort: use order_total field
      else {
        totalAmount = parseFloat(splitPayment.order_total || splitPayment.amount || 0);
      }
    } else {
      // For other payment types, use the existing logic
      totalAmount = parseFloat(splitPayment.order_total || splitPayment.amount || 0);
    }
    
    // Check if current config has payment dates, if not, try stored configuration
    let hasPaymentDates = cfg.after_demo_payment_date || cfg.after_project_payment_date || cfg.payment_date;
    
    if (!hasPaymentDates && details) {
      // Try to get payment dates from stored configuration
      let storedConfig = {};
    
    // Handle string data (try to parse JSON)
    if (typeof details === 'string') {
      try {
        const parsedDetails = JSON.parse(details);
          
          // Try different possible structures
          if (parsedDetails.paymentOptionsConfig) {
            storedConfig = parsedDetails.paymentOptionsConfig;
          } else if (parsedDetails.after_demo_payment_date || parsedDetails.after_project_payment_date || parsedDetails.payment_date) {
            // Payment dates might be stored directly in the root
            storedConfig = parsedDetails;
          } else {
            // Check if there are any date fields in the root
            const dateFields = Object.keys(parsedDetails).filter(key => 
              key.includes('date') || key.includes('payment_date') || key.includes('expiry')
            );
            if (dateFields.length > 0) {
              storedConfig = parsedDetails;
            }
          }
      } catch (e) {
        console.warn('Failed to parse payment option details:', e);
      }
    }
    
    // Handle object data
    if (typeof details === 'object' && details !== null) {
        if (details.paymentOptionsConfig) {
          storedConfig = details.paymentOptionsConfig;
        } else if (details.after_demo_payment_date || details.after_project_payment_date || details.payment_date) {
          storedConfig = details;
        }
      }
      
      // If stored config has payment dates, use it
      if (storedConfig.after_demo_payment_date || storedConfig.after_project_payment_date || storedConfig.payment_date) {
        cfg = { ...cfg, ...storedConfig };
      }
      
      // If we still don't have payment dates, try to calculate them
      if ((!cfg.after_demo_payment_date || cfg.after_demo_payment_date === '') && (!cfg.after_project_payment_date || cfg.after_project_payment_date === '')) {
        // Check if we have any date to calculate from
        // Also check the split payment object itself for dates
        const advancePaymentDate = cfg.payment_date || cfg.advance_payment_date || storedConfig.payment_date || storedConfig.advance_payment_date || splitPayment.created_at || splitPayment.payment_date;
        
        if (advancePaymentDate) {
          // Calculate after demo payment date (10 days after advance payment)
          const advanceDate = new Date(advancePaymentDate);
          const demoDate = new Date(advanceDate.getTime() + (10 * 24 * 60 * 60 * 1000));
          cfg.after_demo_payment_date = demoDate.toISOString().slice(0, 16);
          
          // Calculate after project payment date (15 days after advance payment)
          const projectDate = new Date(advanceDate.getTime() + (15 * 24 * 60 * 60 * 1000));
          cfg.after_project_payment_date = projectDate.toISOString().slice(0, 16);
        }
      }
    }
    

    
    const pNow = parseFloat(cfg.advance_payment_percentage || 50);
    const pAfterDemo = parseFloat(cfg.advance_payment_after_demo_percentage || 25);
    const pAfterProject = parseFloat(cfg.advance_payment_after_project_percentage || 25);
    
    const aNow = (totalAmount * pNow) / 100;
    const aAfterDemo = (totalAmount * pAfterDemo) / 100;
    const aAfterProject = (totalAmount * pAfterProject) / 100;
    
    // Calculate payment dates if not already set
    let afterDemoPaymentDate = cfg.after_demo_payment_date || '';
    let afterProjectPaymentDate = cfg.after_project_payment_date || '';
    
    // If payment dates are not set, calculate them based on advance payment date
    if (!afterDemoPaymentDate && cfg.payment_date) {
      const advanceDate = new Date(cfg.payment_date);
      const demoDate = new Date(advanceDate.getTime() + (10 * 24 * 60 * 60 * 1000)); // 10 days after
      afterDemoPaymentDate = demoDate.toISOString().slice(0, 16);
    }
    
    if (!afterProjectPaymentDate && cfg.payment_date) {
      const advanceDate = new Date(cfg.payment_date);
      const projectDate = new Date(advanceDate.getTime() + (15 * 24 * 60 * 60 * 1000)); // 15 days after
      afterProjectPaymentDate = projectDate.toISOString().slice(0, 16);
    }
    


    // Check if advance payment is completed - be more permissive
    const isAdvanceCompleted = splitPayment.payment_status === 'completed' || 
                              splitPayment.effectivePaymentStatus === 'completed' ||
                              splitPayment.payment_status === 'confirmed' ||
                              splitPayment.order_payment_status === 'completed' ||
                              splitPayment.order_payment_status === 'confirmed';
    


    // Check payment transaction details for completed split payments
    let afterDemoCompleted = false;
    let afterProjectCompleted = false;
    
    if (splitPayment.payment_option_details) {
      let paymentDetails = splitPayment.payment_option_details;
      
      // Handle string data (try to parse JSON)
      if (typeof paymentDetails === 'string') {
        try {
          paymentDetails = JSON.parse(paymentDetails);
        } catch (e) {
          console.warn('Failed to parse payment option details:', e);
        }
      }
      
      // Check if After Demo payment is completed using the new completion flags
      if (paymentDetails.after_demo_completed === true) {
        afterDemoCompleted = true;
      }
      // If stored order status says pending, override completion flag
      if (paymentDetails.after_demo_order_payment_status === 'pending') {
        afterDemoCompleted = false;
      }
      
      // Check if After Project payment is completed using the new completion flags
      if (paymentDetails.after_project_completed === true) {
        afterProjectCompleted = true;

      }
      // If stored order status says pending, override completion flag
      if (paymentDetails.after_project_order_payment_status === 'pending') {
        afterProjectCompleted = false;
      }
    }

    // Find orders for remaining amounts - look for new orders created for split payments
    let afterDemoOrder = splitPaymentOrders?.find(order => 
      (order.payment_option === 'split_payment' || order.payment_option === 'full') &&
      order.payment_option_details?.split_payment_type === 'after_demo' &&
      order.payment_option_details?.original_order_number === splitPayment.order_number
    );



    // If we found an order and it's pending, ensure completion flag is false
    if (afterDemoOrder?.payment_status === 'pending') {
      afterDemoCompleted = false;
    }

    let afterProjectOrder = splitPaymentOrders?.find(order => 
      (order.payment_option === 'split_payment' || order.payment_option === 'full') &&
      order.payment_option_details?.split_payment_type === 'after_project' &&
      order.payment_option_details?.original_order_number === splitPayment.order_number
    );

    // If we found an order and it's pending, ensure completion flag is false
    if (afterProjectOrder?.payment_status === 'pending') {
      afterProjectCompleted = false;
    }





    // Also check if we have completion flags in the original transaction
    if (!afterDemoOrder && afterDemoCompleted) {
      // Check if we have actual order data in payment_option_details
      const hasOrderData = splitPayment.payment_option_details && (() => {
        try {
          const details = typeof splitPayment.payment_option_details === 'string' 
            ? JSON.parse(splitPayment.payment_option_details) 
            : splitPayment.payment_option_details;
          

          
          return details.after_demo_order_id || details.after_demo_order_number;
        } catch (e) {
          return false;
        }
      })();
      
      if (hasOrderData) {
        // Check if the order actually exists in the orders list
        const orderExists = splitPaymentOrders?.some(order => {
          const orderNumber = splitPayment.payment_option_details && (() => {
            try {
              const details = typeof splitPayment.payment_option_details === 'string' 
                ? JSON.parse(splitPayment.payment_option_details) 
                : splitPayment.payment_option_details;
              return details.after_demo_order_number;
            } catch (e) {
              return null;
            }
          })();
          
          return order.order_number === orderNumber;
        });
        

        
        if (hasOrderData && !orderExists) {
          // If we have order data but the order doesn't exist, reset the completion flag
          afterDemoCompleted = false;

        } else if (hasOrderData && orderExists) {

          let paymentStatus = 'completed';
          
          // Try to get the actual payment status from stored data
          if (splitPayment.payment_option_details) {
            let details = splitPayment.payment_option_details;
            if (typeof details === 'string') {
              try {
                details = JSON.parse(details);
              } catch (e) {
                console.warn('Failed to parse payment option details:', e);
              }
            }
            if (details.after_demo_order_payment_status) {
              paymentStatus = details.after_demo_order_payment_status;
            }
          }
          
          afterDemoOrder = {
            payment_status: paymentStatus,
            order_number: `After Demo Payment - ${splitPayment.order_number}`,
            total_amount: aAfterDemo
          };
        }
              } else {
          // If no order data exists, reset the completion flag
          afterDemoCompleted = false;
        }
    }

    if (!afterProjectOrder && afterProjectCompleted) {
      // Check if we have actual order data in payment_option_details
      const hasOrderData = splitPayment.payment_option_details && (() => {
        try {
          const details = typeof splitPayment.payment_option_details === 'string' 
            ? JSON.parse(splitPayment.payment_option_details) 
            : splitPayment.payment_option_details;
          

          
          return details.after_project_order_id || details.after_project_order_number;
        } catch (e) {
          return false;
        }
      })();
      
      if (hasOrderData) {
        // Check if the order actually exists in the orders list
        const orderExists = splitPaymentOrders?.some(order => {
          const orderNumber = splitPayment.payment_option_details && (() => {
            try {
              const details = typeof splitPayment.payment_option_details === 'string' 
                ? JSON.parse(splitPayment.payment_option_details) 
                : splitPayment.payment_option_details;
              return details.after_project_order_number;
            } catch (e) {
              return null;
            }
          })();
          
          return order.order_number === orderNumber;
        });
        

        
        if (hasOrderData && !orderExists) {
          // If we have order data but the order doesn't exist, reset the completion flag
          afterProjectCompleted = false;

        } else if (hasOrderData && orderExists) {

          let paymentStatus = 'completed';
          
          // Try to get the actual payment status from stored data
          if (splitPayment.payment_option_details) {
            let details = splitPayment.payment_option_details;
            if (typeof details === 'string') {
              try {
                details = JSON.parse(details);
              } catch (e) {
                console.warn('Failed to parse payment option details:', e);
              }
            }
            if (details.after_project_order_payment_status) {
              paymentStatus = details.after_project_order_payment_status;
            }
          }
          
          afterProjectOrder = {
            payment_status: paymentStatus,
            order_number: `After Project Payment - ${splitPayment.order_number}`,
            total_amount: aAfterProject
          };
        }
              } else {
          // If no order data exists, reset the completion flag
          afterProjectCompleted = false;
        }
    }

    // Debug logging for split payment #29 - after all processing
    if (splitPayment.id === 29) {
      console.log('🔍 Debug Split Payment #29 - Final Results:', {
        afterDemoOrder,
        afterProjectOrder,
        afterDemoCompleted,
        afterProjectCompleted,
        finalAfterDemoOrder: afterDemoOrder,
        finalAfterProjectOrder: afterProjectOrder
      });
    }



    // Calculate late fees for demo and project payments
    const lateFeePerDay = parseFloat(cfg.late_fee_per_day || 500);
    
    // Calculate late fee for demo payment (only if not completed)
    const demoLateFee = !afterDemoCompleted ? 
      calculateLateFee(cfg.after_demo_payment_date, cfg.after_demo_expiry_date, lateFeePerDay) : 
      { lateFee: 0, daysLate: 0, isLate: false };
    
    // Calculate late fee for project payment (only if not completed)
    const projectLateFee = !afterProjectCompleted ? 
      calculateLateFee(cfg.after_project_payment_date, cfg.after_project_expiry_date, lateFeePerDay) : 
      { lateFee: 0, daysLate: 0, isLate: false };





    return {
      advanceAmount: aNow,
      afterDemoAmount: aAfterDemo + demoLateFee.lateFee,
      afterProjectAmount: aAfterProject + projectLateFee.lateFee,
      afterDemoText: cfg.advance_payment_after_demo_text || 'After Demo Completion',
      afterProjectText: cfg.advance_payment_after_project_text || 'After Project Completion',
      isAdvanceCompleted,
      afterDemoOrder,
      afterProjectOrder,
      afterDemoCompleted,
      afterProjectCompleted,
              totalAmount,
        // Add payment dates and fine info
        afterDemoPaymentDate: afterDemoPaymentDate,
        afterProjectPaymentDate: afterProjectPaymentDate,
        afterDemoExpiryDate: cfg.after_demo_expiry_date || '',
        afterProjectExpiryDate: cfg.after_project_expiry_date || '',
      lateFeePerDay: lateFeePerDay,
      // Add late fee details
      demoLateFee: demoLateFee,
      projectLateFee: projectLateFee,
      // Original amounts without late fees
      originalAfterDemoAmount: aAfterDemo,
      originalAfterProjectAmount: aAfterProject
    };
  };



  const mockInvoices = [
    {
      id: 1,
      invoice_number: 'INV-001',
      customer_name: 'John Doe',
      customer_email: 'john@example.com',
      total_amount: 50000,
      payment_method: 'Credit Card',
      payment_status: 'completed',
      order_status: 'completed',
      created_at: new Date().toISOString(),
      due_date: new Date(Date.now() + 86400000 * 30).toISOString(),
      description: 'Website Development Invoice'
    },
    {
      id: 2,
      invoice_number: 'INV-002',
      customer_name: 'Jane Smith',
      customer_email: 'jane@example.com',
      total_amount: 75000,
      payment_method: 'Bank Transfer',
      payment_status: 'pending',
      order_status: 'in_progress',
      created_at: new Date(Date.now() - 86400000).toISOString(),
      due_date: new Date(Date.now() + 86400000 * 15).toISOString(),
      description: 'Mobile App Development Invoice'
    }
  ];

  const mockQuotes = [
    {
      id: 1,
      project_name: 'E-commerce Website',
      client_name: 'John Doe',
      client_email: 'john@example.com',
      amount: 50000,
      valid_until: new Date(Date.now() + 86400000 * 30).toISOString(),
      status: 'pending',
      created_at: new Date().toISOString(),
      description: 'Complete e-commerce website development with payment integration'
    },
    {
      id: 2,
      project_name: 'Mobile App',
      client_name: 'Jane Smith',
      client_email: 'jane@example.com',
      amount: 75000,
      valid_until: new Date(Date.now() + 86400000 * 15).toISOString(),
      status: 'accepted',
      created_at: new Date(Date.now() - 86400000).toISOString(),
      description: 'Cross-platform mobile application development'
    }
  ];

  const mockAppointments = [
    {
      id: 1,
      name: 'John Doe',
      purpose: 'Project Discussion',
      message: 'Initial meeting to discuss project requirements and timeline',
      preferred_date: new Date().toISOString().split('T')[0],
      preferred_time: '10:00',
      phone: '+91 9876543210',
      status: 'confirmed',
      created_at: new Date().toISOString()
    },
    {
      id: 2,
      name: 'Jane Smith',
      purpose: 'Design Review',
      message: 'Review of website design mockups and feedback session',
      preferred_date: new Date(Date.now() + 86400000 * 2).toISOString().split('T')[0],
      preferred_time: '14:30',
      phone: '+91 9876543211',
      status: 'pending',
      created_at: new Date(Date.now() - 86400000).toISOString()
    }
  ];

  // Use mock data if API data is empty (for testing)

  const displayInvoices = Array.isArray(invoices) && invoices.length > 0 ? invoices : mockInvoices;
  const displayQuotes = Array.isArray(quotes) && quotes.length > 0 ? quotes : mockQuotes;
  const displayAppointments = Array.isArray(appointments) && appointments.length > 0 ? appointments : mockAppointments;

  const handleLogout = async () => {
    await logout();
  };

  // Helper function to format payment option for display
  const formatPaymentOption = (paymentOption) => {
    if (!paymentOption) return 'Full Payment';
    
    switch (paymentOption.toLowerCase()) {
      case 'full':
      case 'full_payment':
        return 'Full Payment';
      case 'advance':
      case 'advance_payment':
        return 'Split Payment';
      case 'emi':
      case 'emi_payment':
        return 'EMI Payment';
      case 'split':
      case 'split_payment':
        return 'Split Payment';
      default:
        return paymentOption.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };
  // Handle making EMI/Split payments
  const handleMakePayment = (payment) => {
    // Helper function to get payment type text
    const getPaymentTypeText = () => {
      return getPaymentTypeWithInstallment(payment.transaction_type, payment.installment_number);
    };
    
    // Calculate payment amount based on status
    let paymentAmount = parseFloat(payment.next_payment_amount || payment.remaining_amount || 0);
    let isEarlyPayment = false;
    let interestWaiverAmount = 0;
    let originalAmount = paymentAmount;
    
    // If status is "Coming Soon", calculate amount without interest (early payment)
    if (payment.installment_status === 'coming_soon' && payment.transaction_type === 'emi_payment') {
      isEarlyPayment = true;
      
      // Calculate installment amount without interest
      const totalAmount = parseFloat(payment.order_total || 0);
      const emiAmountWithInterest = parseFloat(payment.amount || 0);
      const totalInstallments = parseInt(payment.total_installments || 6);
      
      // Calculate principal amount (total without interest)
      const principalAmount = totalAmount;
      
      // Calculate principal per installment (without interest)
      const principalPerInstallment = principalAmount / totalInstallments;
      
      // Set payment amount to principal only (interest waived for early payment)
      paymentAmount = Math.round(principalPerInstallment * 100) / 100; // Round to 2 decimal places
      interestWaiverAmount = originalAmount - paymentAmount;
      
      console.log('💰 Early Payment Calculation:', {
        totalAmount,
        emiAmountWithInterest,
        principalAmount,
        totalInstallments,
        principalPerInstallment,
        originalAmount,
        paymentAmount,
        interestWaiverAmount
      });
    }
    
    // Create a cart item for the payment
    const cartItem = {
      id: `payment_${payment.id}`,
      name: `${getPaymentTypeWithInstallment(payment.transaction_type, payment.installment_number)} - Order #${payment.order_number}${isEarlyPayment ? ' (Early Payment)' : ''}`,
      price: originalAmount,
      calculatedPrice: paymentAmount,
      quantity: 1,
      type: 'payment',
      paymentData: {
        type: payment.transaction_type,
        amount: paymentAmount,
        original_amount: originalAmount,
        order_id: payment.order_id,
        transaction_id: payment.id,
        order_number: payment.order_number,
        payment_status: payment.payment_status,
        installment_status: payment.installment_status,
        payment_method: payment.payment_method,
        next_payment_date: payment.next_payment_date,
        due_date: payment.due_date,
        total_amount: payment.order_total,
        paid_amount: (() => {
          // For EMI payments, paid_amount should be the total amount paid so far
          // This includes all completed installments
          if (payment.transaction_type === 'emi_payment') {
            // For EMI, we need to calculate based on completed installments
            // For now, use the current installment amount as paid
            return payment.amount;
          }
          return payment.amount;
        })(),
        remaining_amount: payment.remaining_amount,
        current_installment: payment.current_installment,
        total_installments: payment.total_installments,
        installment_number: payment.installment_number,
        interest_waived: isEarlyPayment,
        interest_waiver_amount: interestWaiverAmount,
        early_payment_date: isEarlyPayment ? new Date().toISOString() : null
      },
      // Add product-like properties for cart display
      product_image: null,
      category: 'Payment',
      description: `Payment for ${getPaymentTypeText()} - Order #${payment.order_number}${isEarlyPayment ? ` (Early payment with ₹${interestWaiverAmount.toFixed(2)} interest waiver)` : ''}`,
      regular_license_price: originalAmount,
      extended_license_price: paymentAmount,
      discount_percentage: isEarlyPayment ? Math.round((interestWaiverAmount / originalAmount) * 100) : 0,
      discount_amount: interestWaiverAmount,
      final_price: paymentAmount
    };
    
    // Add to cart
    addToCart(cartItem);
    
    // Show success message
    const message = isEarlyPayment 
      ? `${getPaymentTypeText()} has been added to your cart with ₹${interestWaiverAmount.toFixed(2)} interest waiver for early payment!`
      : `${getPaymentTypeText()} has been added to your cart!`;
    alert(message);
    
    // Navigate to cart
    navigate('/cart');
  };

  // Handle making Split Payment remaining amounts
  const handleMakeSplitPayment = (payment, paymentType, amount, paymentText) => {
    // Get coupon information from the original order
    let originalCoupon = null;
    if (payment.order_id && orders.length > 0) {
      const originalOrder = orders.find(order => order.id === payment.order_id);
      
      if (originalOrder && originalOrder.applied_coupon) {
        try {
          originalCoupon = typeof originalOrder.applied_coupon === 'string' 
            ? JSON.parse(originalOrder.applied_coupon) 
            : originalOrder.applied_coupon;
        } catch (e) {
          console.warn('Failed to parse original order coupon:', e);
        }
      }
    }

    // Store original amount - cart will handle discount calculation when coupon is applied
    let originalAmount = amount;
    let discountedAmount = amount; // Will be updated by cart when coupon is applied

    // Create a cart item for the split payment
    const cartItem = {
      id: `split_payment_${payment.id}_${paymentType}`,
      name: `${paymentText} - Order #${payment.order_number}`,
      price: originalAmount, // Original amount before discount
      calculatedPrice: originalAmount, // Will be updated by cart when coupon is applied
      quantity: 1,
      type: 'split_payment',
      paymentData: {
        type: 'split_payment',
        amount: originalAmount, // Use original amount - cart will handle discount
        original_amount: originalAmount, // Store original amount
        order_id: payment.order_id,
        transaction_id: payment.id,
        order_number: payment.order_number,
        payment_status: 'pending',
        payment_method: payment.payment_method,
        split_payment_type: paymentType,
        split_payment_text: paymentText,
        parent_split_payment_id: payment.id,
        total_amount: payment.order_total,
        paid_amount: (() => {
          // Calculate total paid amount: Advance Amount + Split Paid Amount
          const remainingAmounts = getRemainingSplitPaymentAmounts(payment);
          if (!remainingAmounts) {
            return payment.amount; // Fallback to advance amount
          }
          
          let totalPaid = Number(remainingAmounts.advanceAmount || 0);
          
          // Add demo amount if completed
          if (remainingAmounts.afterDemoCompleted || 
              remainingAmounts.afterDemoOrder?.payment_status === 'completed') {
            totalPaid += remainingAmounts.afterDemoAmount;
          }
          
          // Add project amount if completed
          if (remainingAmounts.afterProjectCompleted || 
              remainingAmounts.afterProjectOrder?.payment_status === 'completed') {
            totalPaid += remainingAmounts.afterProjectAmount;
          }
          
          return totalPaid;
        })(),
        remaining_amount: originalAmount, // Use original amount - cart will handle discount
        // Include original coupon information
        original_coupon: originalCoupon
      },
      // Add product-like properties for cart display
      product_image: null,
      category: 'Split Payment',
      description: `${paymentText} payment for Order #${payment.order_number}`,
      regular_license_price: originalAmount, // Original price
      extended_license_price: originalAmount,
      discount_percentage: 0, // Will be set by cart when coupon is applied
      discount_amount: 0, // Will be set by cart when coupon is applied
      final_price: originalAmount // Will be updated by cart when coupon is applied
    };
    
    // Add to cart
    addToCart(cartItem);
    
    // Show success message
    alert(`${paymentText} payment has been added to your cart!`);
    
    // Navigate to cart
    navigate('/cart');
  };

  const toggleOrderExpansion = (orderId) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  const openOrderDetails = (order) => {
    // Ensure order has items property, even if empty
    const orderWithItems = {
      ...order,
      items: order.items || []
    };
    setSelectedOrder(orderWithItems);
    setShowOrderModal(true);
  };

  const closeOrderModal = () => {
    setShowOrderModal(false);
    setSelectedOrder(null);
  };

  // Order PDF download and print handlers
  const handleDownloadOrderPDF = async (order) => {
    try {
      console.log('🔍 Downloading order PDF for order:', order.id);
      
      const response = await pdfGenerationAPI.generateOrderPDF(order.id);
      
      if (response?.data) {
        const blob = response.data;
        
        if (blob.size === 0) {
          throw new Error('PDF blob is empty');
        }

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `order-${order.order_number}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        console.log('✅ Order PDF downloaded successfully');
      } else {
        throw new Error('No PDF data received');
      }
    } catch (error) {
      console.error('❌ Error downloading order PDF:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to download order PDF. ';
      if (error.response?.status === 404) {
        errorMessage += 'Order not found.';
      } else if (error.response?.status === 401) {
        errorMessage += 'Please log in again.';
      } else if (error.response?.status === 500) {
        errorMessage += 'Server error. Please try again later.';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage += 'Network error. Please check your connection.';
      } else {
        errorMessage += error.message || 'Unknown error occurred.';
      }
      
      alert(errorMessage);
    }
  };

  const handlePrintOrder = async (order) => {
    try {
      console.log('🔍 Printing order for order:', order.id);
      
      const response = await pdfGenerationAPI.generateOrderPDF(order.id);
      
      if (response?.data) {
        const blob = response.data;
        
        if (blob.size === 0) {
          throw new Error('PDF blob is empty');
        }

        // Create blob URL and open in new window for printing
        const url = window.URL.createObjectURL(blob);
        const printWindow = window.open(url, '_blank');
        
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
            // Clean up the blob URL after printing
            setTimeout(() => {
              window.URL.revokeObjectURL(url);
            }, 1000);
          };
        } else {
          throw new Error('Popup blocked. Please allow popups for this site.');
        }
        
        console.log('✅ Order PDF opened for printing');
      } else {
        throw new Error('No PDF data received');
      }
    } catch (error) {
      console.error('❌ Error printing order:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to print order. ';
      if (error.response?.status === 404) {
        errorMessage += 'Order not found.';
      } else if (error.response?.status === 401) {
        errorMessage += 'Please log in again.';
      } else if (error.response?.status === 500) {
        errorMessage += 'Server error. Please try again later.';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage += 'Network error. Please check your connection.';
      } else if (error.message.includes('Popup blocked')) {
        errorMessage += 'Please allow popups for this site.';
      } else {
        errorMessage += error.message || 'Unknown error occurred.';
      }
      
      alert(errorMessage);
    }
  };

  // Invoice PDF download and print handlers
  const handleDownloadInvoicePDF = async (invoice) => {
    try {
      console.log('🔍 Downloading invoice PDF for invoice:', invoice.id);
      
      const response = await invoiceAPI.downloadPDF(invoice.id);
      
      if (response?.data) {
        const blob = response.data;
        
        if (blob.size === 0) {
          throw new Error('PDF blob is empty');
        }

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `invoice-${invoice.invoice_number || invoice.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        console.log('✅ Invoice PDF downloaded successfully');
      } else {
        throw new Error('No PDF data received');
      }
    } catch (error) {
      console.error('❌ Error downloading invoice PDF:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to download invoice PDF. ';
      if (error.response?.status === 404) {
        errorMessage += 'Invoice not found.';
      } else if (error.response?.status === 401) {
        errorMessage += 'Please log in again.';
      } else if (error.response?.status === 500) {
        errorMessage += 'Server error. Please try again later.';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage += 'Network error. Please check your connection.';
      } else {
        errorMessage += error.message || 'Unknown error occurred.';
      }
      
      alert(errorMessage);
    }
  };

  const handlePrintInvoice = async (invoice) => {
    try {
      console.log('🔍 Printing invoice for invoice:', invoice.id);
      
      const response = await invoiceAPI.downloadPDF(invoice.id);
      
      if (response?.data) {
        const blob = response.data;
        
        if (blob.size === 0) {
          throw new Error('PDF blob is empty');
        }

        // Create blob URL and open in new window for printing
        const url = window.URL.createObjectURL(blob);
        const printWindow = window.open(url, '_blank');
        
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
            // Clean up the blob URL after printing
            setTimeout(() => {
              window.URL.revokeObjectURL(url);
            }, 1000);
          };
        } else {
          throw new Error('Popup blocked. Please allow popups for this site.');
        }
        
        console.log('✅ Invoice PDF opened for printing');
      } else {
        throw new Error('No PDF data received');
      }
    } catch (error) {
      console.error('❌ Error printing invoice:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to print invoice. ';
      if (error.response?.status === 404) {
        errorMessage += 'Invoice not found.';
      } else if (error.response?.status === 401) {
        errorMessage += 'Please log in again.';
      } else if (error.response?.status === 500) {
        errorMessage += 'Server error. Please try again later.';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage += 'Network error. Please check your connection.';
      } else if (error.message.includes('Popup blocked')) {
        errorMessage += 'Please allow popups for this site.';
      } else {
        errorMessage += error.message || 'Unknown error occurred.';
      }
      
      alert(errorMessage);
    }
  };

  // Quote PDF download and print handlers
  const handleDownloadQuotePDF = async (quote) => {
    try {
      console.log('🔍 Downloading quote PDF for quote:', quote.id);
      
      const response = await quoteAPI.downloadPDF(quote.id);
      
      if (response?.data) {
        const blob = response.data;
        
        if (blob.size === 0) {
          throw new Error('PDF blob is empty');
        }

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `quote-${quote.id}-${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        console.log('✅ Quote PDF downloaded successfully');
      } else {
        throw new Error('No PDF data received');
      }
    } catch (error) {
      console.error('❌ Error downloading quote PDF:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to download quote PDF. ';
      if (error.response?.status === 404) {
        errorMessage += 'Quote not found.';
      } else if (error.response?.status === 401) {
        errorMessage += 'Please log in again.';
      } else if (error.response?.status === 500) {
        errorMessage += 'Server error. Please try again later.';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage += 'Network error. Please check your connection.';
      } else {
        errorMessage += error.message || 'Unknown error occurred.';
      }
      
      alert(errorMessage);
    }
  };

  const handlePrintQuote = async (quote) => {
    try {
      console.log('🔍 Printing quote for quote:', quote.id);
      
      const response = await quoteAPI.downloadPDF(quote.id);
      
      if (response?.data) {
        const blob = response.data;
        
        if (blob.size === 0) {
          throw new Error('PDF blob is empty');
        }

        // Create blob URL and open in new window for printing
        const url = window.URL.createObjectURL(blob);
        const printWindow = window.open(url, '_blank');
        
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
            // Clean up the blob URL after printing
            setTimeout(() => {
              window.URL.revokeObjectURL(url);
            }, 1000);
          };
        } else {
          throw new Error('Popup blocked. Please allow popups for this site.');
        }
        
        console.log('✅ Quote PDF opened for printing');
      } else {
        throw new Error('No PDF data received');
      }
    } catch (error) {
      console.error('❌ Error printing quote:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to print quote. ';
      if (error.response?.status === 404) {
        errorMessage += 'Quote not found.';
      } else if (error.response?.status === 401) {
        errorMessage += 'Please log in again.';
      } else if (error.response?.status === 500) {
        errorMessage += 'Server error. Please try again later.';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage += 'Network error. Please check your connection.';
      } else if (error.message.includes('Popup blocked')) {
        errorMessage += 'Please allow popups for this site.';
      } else {
        errorMessage += error.message || 'Unknown error occurred.';
      }
      
      alert(errorMessage);
    }
  };

  // Appointment PDF download and print handlers
  const handleDownloadAppointmentPDF = async (appointment) => {
    try {
      console.log('🔍 Downloading appointment PDF for appointment:', appointment.id);
      
      const response = await pdfGenerationAPI.generateAppointmentPDF(appointment.id);
      
      if (response?.data) {
        const blob = response.data;
        
        if (blob.size === 0) {
          throw new Error('PDF blob is empty');
        }

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `appointment-${appointment.id}-${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        console.log('✅ Appointment PDF downloaded successfully');
      } else {
        throw new Error('No PDF data received');
      }
    } catch (error) {
      console.error('❌ Error downloading appointment PDF:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to download appointment PDF. ';
      if (error.response?.status === 404) {
        errorMessage += 'Appointment not found.';
      } else if (error.response?.status === 401) {
        errorMessage += 'Please log in again.';
      } else if (error.response?.status === 500) {
        errorMessage += 'Server error. Please try again later.';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage += 'Network error. Please check your connection.';
      } else {
        errorMessage += error.message || 'Unknown error occurred.';
      }
      
      alert(errorMessage);
    }
  };

  const handlePrintAppointment = async (appointment) => {
    try {
      console.log('🔍 Printing appointment for appointment:', appointment.id);
      
      const response = await pdfGenerationAPI.generateAppointmentPDF(appointment.id);
      
      if (response?.data) {
        const blob = response.data;
        
        if (blob.size === 0) {
          throw new Error('PDF blob is empty');
        }

        // Create blob URL and open in new window for printing
        const url = window.URL.createObjectURL(blob);
        const printWindow = window.open(url, '_blank');
        
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
            // Clean up the blob URL after printing
            setTimeout(() => {
              window.URL.revokeObjectURL(url);
            }, 1000);
          };
        } else {
          throw new Error('Popup blocked. Please allow popups for this site.');
        }
        
        console.log('✅ Appointment PDF opened for printing');
      } else {
        throw new Error('No PDF data received');
      }
    } catch (error) {
      console.error('❌ Error printing appointment:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to print appointment. ';
      if (error.response?.status === 404) {
        errorMessage += 'Appointment not found.';
      } else if (error.response?.status === 401) {
        errorMessage += 'Please log in again.';
      } else if (error.response?.status === 500) {
        errorMessage += 'Server error. Please try again later.';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage += 'Network error. Please check your connection.';
      } else if (error.message.includes('Popup blocked')) {
        errorMessage += 'Please allow popups for this site.';
      } else {
        errorMessage += error.message || 'Unknown error occurred.';
      }
      
      alert(errorMessage);
    }
  };

  // Project creation handlers
  const handleCreateProject = () => {
    setShowProjectModal(true);
    setProjectError(null);
  };

  const handleProjectSubmit = async (formData) => {
    try {
      setProjectLoading(true);
      setProjectError(null);

      console.log('🔍 Creating project with data:', formData);
      
      const response = await projectAPI.create(formData);

      if (response?.data?.success) {
        console.log('✅ Project created successfully');
        setShowProjectModal(false);
        // Refetch projects to get updated list
        await refetchProjects();
      } else {
        throw new Error(response?.data?.message || 'Failed to create project');
      }
    } catch (err) {
      console.error('Error creating project:', err);
      setProjectError(`Error creating project: ${err.response?.data?.message || err.message}`);
    } finally {
      setProjectLoading(false);
    }
  };

  const closeProjectModal = () => {
    setShowProjectModal(false);
    setProjectError(null);
  };

  // Quote creation handlers
  const handleCreateQuote = () => {
    setShowQuoteModal(true);
    setQuoteError(null);
  };

  const handleQuoteSubmit = async (formData) => {
    try {
      setQuoteLoading(true);
      setQuoteError(null);

      console.log('🔍 Creating quote with data:', formData);
      
      const response = await quoteAPI.create(formData);

      if (response?.data?.success) {
        console.log('✅ Quote created successfully');
        setShowQuoteModal(false);
        // Refetch quotes to get updated list
        await refetchQuotes();
      } else {
        throw new Error(response?.data?.message || 'Failed to create quote');
      }
    } catch (err) {
      console.error('Error creating quote:', err);
      setQuoteError(`Error creating quote: ${err.response?.data?.message || err.message}`);
    } finally {
      setQuoteLoading(false);
    }
  };

  const closeQuoteModal = () => {
    setShowQuoteModal(false);
    setQuoteError(null);
  };

  // Appointment booking handlers
  const handleBookAppointment = () => {
    setShowAppointmentModal(true);
    setAppointmentError(null);
  };

  const handleAppointmentSubmit = async (formData) => {
    try {
      setAppointmentLoading(true);
      setAppointmentError(null);

      console.log('🔍 Booking appointment with data:', formData);
      
      const response = await appointmentAPI.create(formData);

      if (response?.data?.success) {
        console.log('✅ Appointment booked successfully');
        setShowAppointmentModal(false);
        // Refetch appointments to get updated list
        await refetchAppointments();
      } else {
        throw new Error(response?.data?.message || 'Failed to book appointment');
      }
    } catch (err) {
      console.error('Error booking appointment:', err);
      setAppointmentError(`Error booking appointment: ${err.response?.data?.message || err.message}`);
    } finally {
      setAppointmentLoading(false);
    }
  };

  const closeAppointmentModal = () => {
    setShowAppointmentModal(false);
    setAppointmentError(null);
  };



  // Invoice details handlers
  const openInvoiceDetails = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };

  const closeInvoiceModal = () => {
    setShowInvoiceModal(false);
    setSelectedInvoice(null);
  };

  // Quote details handlers
  const openQuoteDetails = (quote) => {
    setSelectedQuote(quote);
    setShowQuoteDetailsModal(true);
  };

  const closeQuoteDetailsModal = () => {
    setShowQuoteDetailsModal(false);
    setSelectedQuote(null);
  };

  // Appointment details handlers
  const openAppointmentDetails = (appointment) => {
    setSelectedAppointment(appointment);
    setShowAppointmentDetailsModal(true);
  };

  const closeAppointmentDetailsModal = () => {
    setShowAppointmentDetailsModal(false);
    setSelectedAppointment(null);
  };



  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date) => {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }) + ' ' + dateObj.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const dashboardSections = [
    { id: 'overview', label: 'Overview', shortLabel: 'Overview', icon: Activity },
    { id: 'orders', label: 'Orders', shortLabel: 'Orders', icon: FileText },
    { id: 'emi-split-payments', label: 'EMIs and Split Payments', shortLabel: 'EMI/Split', icon: CreditCard },
    { id: 'appointments', label: 'Appointments', shortLabel: 'Appointments', icon: Calendar },
    { id: 'coupons', label: 'My Coupons', shortLabel: 'Coupons', icon: Tag },
    { id: 'profile', label: 'Profile', shortLabel: 'Profile', icon: User },
    { id: 'console', label: 'My Console', shortLabel: 'Console', icon: Monitor }
  ];
  const renderOverview = () => (
    <div className="space-y-6 sm:space-y-8">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
        <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-4 sm:p-6 md:p-8 hover:scale-105 transition-all duration-300">
          <div className="flex items-center">
            <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg">
              <FileText className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-white" />
            </div>
            <div className="ml-4 sm:ml-6">
              <p className="text-sm sm:text-base md:text-lg font-bold text-gray-600 dark:text-gray-400 mb-1">
                Total Orders
              </p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                {ordersLoading ? '...' : (Array.isArray(orders) ? orders.length : 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-4 sm:p-6 md:p-8 hover:scale-105 transition-all duration-300">
          <div className="flex items-center">
            <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg">
              <CheckCircle className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-white" />
            </div>
            <div className="ml-4 sm:ml-6">
              <p className="text-sm sm:text-base md:text-lg font-bold text-gray-600 dark:text-gray-400 mb-1">
                Completed Orders
              </p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                {ordersLoading ? '...' : (Array.isArray(orders) ? orders.filter(o => o.order_status === 'completed').length : 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-4 sm:p-6 md:p-8 hover:scale-105 transition-all duration-300">
          <div className="flex items-center">
            <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg">
              <Calendar className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-white" />
            </div>
            <div className="ml-4 sm:ml-6">
              <p className="text-sm sm:text-base md:text-lg font-bold text-gray-600 dark:text-gray-400 mb-1">
                Total Appointments
              </p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                {appointmentsLoading ? '...' : (Array.isArray(appointments) ? appointments.length : 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-4 sm:p-6 md:p-8 hover:scale-105 transition-all duration-300">
          <div className="flex items-center">
            <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg">
              <Clock className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-white" />
            </div>
            <div className="ml-4 sm:ml-6">
              <p className="text-sm sm:text-base md:text-lg font-bold text-gray-600 dark:text-gray-400 mb-1">
                Pending Appointments
              </p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                {appointmentsLoading ? '...' : (Array.isArray(appointments) ? appointments.filter(a => a.status === 'pending').length : 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-4 sm:p-6 md:p-8 hover:scale-105 transition-all duration-300">
          <div className="flex items-center">
            <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg">
              <DollarSign className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-white" />
            </div>
            <div className="ml-4 sm:ml-6">
              <p className="text-sm sm:text-base md:text-lg font-bold text-gray-600 dark:text-gray-400 mb-1">
                Total Order Value
              </p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                {ordersLoading ? '...' : (() => {
                  if (!Array.isArray(orders) || orders.length === 0) {
                    return formatCurrency(0);
                  }
                  
                  const total = orders.reduce((sum, order) => {
                    // Handle various possible field names and data types
                    let amount = 0;
                    if (order.total_amount !== null && order.total_amount !== undefined) {
                      amount = parseFloat(order.total_amount) || 0;
                    } else if (order.amount !== null && order.amount !== undefined) {
                      amount = parseFloat(order.amount) || 0;
                    }
                    
                    console.log('Order amount calculation:', { 
                      orderId: order.id, 
                      total_amount: order.total_amount, 
                      amount: order.amount, 
                      parsedAmount: amount,
                      isNaN: isNaN(amount)
                    });
                    
                    return sum + (isNaN(amount) ? 0 : amount);
                  }, 0);
                  
                  console.log('Total order value:', total);
                  return formatCurrency(total);
                })()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
        <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20">
          <div className="p-4 sm:p-6 md:p-8 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
              Recent Orders
            </h3>
          </div>
          <div className="p-4 sm:p-6 md:p-8">
            {ordersLoading ? (
              <div className="space-y-4 sm:space-y-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-5 sm:h-6 bg-gray-200 dark:bg-gray-700 rounded-2xl w-3/4 mb-2 sm:mb-3"></div>
                    <div className="h-3 sm:h-4 bg-gray-200 dark:bg-gray-700 rounded-2xl w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : (Array.isArray(orders) && orders.length > 0) ? (
              <div className="space-y-4 sm:space-y-6">
                {orders.slice(0, 3).map((order) => (
                  <div key={order.id} className="flex items-center justify-between p-4 sm:p-6 border-2 border-gray-200 dark:border-gray-700 rounded-xl sm:rounded-2xl hover:bg-white/50 dark:hover:bg-white/5 transition-all duration-300 hover:scale-105 shadow-lg">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 truncate">
                        Order #{order.order_number || order.id}
                      </h4>
                      <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
                        {formatDate(order.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 sm:space-x-3 ml-2">
                      <span className={`px-2 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-bold ${getStatusColor(order.order_status, 'order')}`}>
                        {mapOrderStatusToDisplay(order.order_status)}
                      </span>
                      <button
                        onClick={() => openOrderDetails(order)}
                        className="p-2 sm:p-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-xl sm:rounded-2xl transition-all duration-300 touch-manipulation"
                      >
                        <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 sm:py-12">
                <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center shadow-lg">
                  <Package className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white" />
                </div>
                <p className="text-lg sm:text-xl font-bold text-gray-500 dark:text-gray-400">
                No orders found
              </p>
              </div>
            )}
          </div>
        </div>

        <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20">
          <div className="p-4 sm:p-6 md:p-8 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              Recent Appointments
            </h3>
          </div>
          <div className="p-4 sm:p-6 md:p-8">
            {appointmentsLoading ? (
              <div className="space-y-4 sm:space-y-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-5 sm:h-6 bg-gray-200 dark:bg-gray-700 rounded-2xl w-3/4 mb-2 sm:mb-3"></div>
                    <div className="h-3 sm:h-4 bg-gray-200 dark:bg-gray-700 rounded-2xl w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : (Array.isArray(appointments) && appointments.length > 0) ? (
              <div className="space-y-4 sm:space-y-6">
                {appointments.slice(0, 3).map((appointment) => (
                  <div key={appointment.id} className="flex items-center justify-between p-4 sm:p-6 border-2 border-gray-200 dark:border-gray-700 rounded-xl sm:rounded-2xl hover:bg-white/50 dark:hover:bg-white/5 transition-all duration-300 hover:scale-105 shadow-lg">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 truncate">
                        {appointment.purpose || `Appointment #${appointment.id}`}
                      </h4>
                      <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
                        {formatDate(appointment.preferred_date)} at {appointment.preferred_time}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 sm:space-x-3 ml-2">
                      <span className={`px-2 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-bold ${getStatusColor(appointment.status)}`}>
                        {appointment.status || 'Unknown'}
                      </span>
                      <button
                        onClick={() => openAppointmentDetails(appointment)}
                        className="p-2 sm:p-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-xl sm:rounded-2xl transition-all duration-300 touch-manipulation"
                      >
                        <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 sm:py-12">
                <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center shadow-lg">
                  <Calendar className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white" />
                </div>
                <p className="text-lg sm:text-xl font-bold text-gray-500 dark:text-gray-400">
                No appointments found
              </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderProjects = () => (
    <div className="space-y-6 sm:space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">My Projects</h2>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
          <div className="relative">
            <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full sm:w-auto pl-10 sm:pl-12 pr-4 sm:pr-6 py-3 sm:py-4 text-sm sm:text-base border-2 border-gray-300 dark:border-gray-600 rounded-xl sm:rounded-2xl bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 shadow-lg transition-all duration-300"
            />
          </div>
          <button 
            onClick={handleCreateProject}
            className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 sm:px-6 py-3 rounded-xl sm:rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow flex items-center justify-center space-x-2 sm:space-x-3 touch-manipulation"
          >
            <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="text-sm sm:text-base">New Project</span>
          </button>
        </div>
      </div>

      <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20">
        <div className="p-4 sm:p-6 md:p-8">
          {projectsLoading ? (
            <div className="space-y-4 sm:space-y-6">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-5 sm:h-6 bg-gray-200 dark:bg-gray-700 rounded-2xl w-3/4 mb-2 sm:mb-3"></div>
                  <div className="h-3 sm:h-4 bg-gray-200 dark:bg-gray-700 rounded-2xl w-1/2"></div>
                </div>
              ))}
            </div>
          ) : (Array.isArray(projects) && projects.length > 0) ? (
            <div className="space-y-4 sm:space-y-6">
              {projects.map((project) => (
                <div key={project.id} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-4 sm:p-6 md:p-8 hover:scale-105 transition-all duration-300">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 truncate">
                        {project.title}
                      </h3>
                      <p className="text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-400 mb-3 sm:mb-4 line-clamp-2">
                        {project.description}
                      </p>
                      <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-6 text-sm sm:text-base text-gray-500 dark:text-gray-400">
                        <span className="font-semibold">Created: {formatDate(project.created_at)}</span>
                        <span className="font-semibold">Budget: {formatCurrency(project.budget || 0)}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 sm:space-x-3 w-full sm:w-auto">
                      <span className={`px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-bold ${getStatusColor(project.status)}`}>
                        {project.status.replace('_', ' ')}
                      </span>
                      <button
                        onClick={() => navigate(`/dashboard/projects/${project.id}`)}
                        className="p-2 sm:p-3 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-xl sm:rounded-2xl transition-all duration-300 touch-manipulation"
                      >
                        <Eye className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" />
                      </button>
                      <button className="p-2 sm:p-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-xl sm:rounded-2xl transition-all duration-300 touch-manipulation">
                        <MoreHorizontal className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 sm:py-12">
              <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center shadow-lg">
                <Briefcase className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">No projects yet</h3>
              <p className="text-base sm:text-lg text-gray-500 dark:text-gray-400 mb-6 sm:mb-8">
                Start your first project to see it here
              </p>
              <button 
                onClick={handleCreateProject}
                className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow touch-manipulation"
              >
                Create Project
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
  const renderOrders = () => (
    <div className="space-y-6 sm:space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">My Orders</h2>
        <div className="flex items-center space-x-4 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full sm:w-auto pl-10 sm:pl-12 pr-4 sm:pr-6 py-3 sm:py-4 text-sm sm:text-base border-2 border-gray-300 dark:border-gray-600 rounded-xl sm:rounded-2xl bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 shadow-lg transition-all duration-300"
            />
          </div>
        </div>
      </div>

      <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20">
        <div className="p-4 sm:p-6 md:p-8">
          {ordersError ? (
            <div className="text-center py-8 sm:py-12">
              <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center shadow-lg">
                <AlertCircle className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">Error loading orders</h3>
              <p className="text-base sm:text-lg text-gray-500 dark:text-gray-400 mb-6 sm:mb-8">
                {ordersError}
              </p>
              <button 
                onClick={() => window.location.reload()}
                className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow touch-manipulation"
              >
                Retry
              </button>
            </div>
          ) : ordersLoading ? (
            <div className="space-y-4 sm:space-y-6">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-5 sm:h-6 bg-gray-200 dark:bg-gray-700 rounded-2xl w-3/4 mb-2 sm:mb-3"></div>
                  <div className="h-3 sm:h-4 bg-gray-200 dark:bg-gray-700 rounded-2xl w-1/2"></div>
                </div>
              ))}
            </div>
          ) : (Array.isArray(orders) && orders.length > 0) ? (
            <div className="space-y-4 sm:space-y-6">
              {orders.map((order) => (
                <div key={order.id} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 hover:scale-105 transition-all duration-300">
                  <div className="p-4 sm:p-6 md:p-8">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 truncate">
                          Order #{order.order_number}
                        </h3>
                        <p className="text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-400 mb-3 sm:mb-4 truncate">
                          {order.customer_name} • {order.customer_email}
                        </p>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 md:space-x-6 text-sm sm:text-base text-gray-500 dark:text-gray-400">
                          <span className="font-semibold">Created: {formatDate(order.created_at)}</span>
                          <span className="font-semibold">Amount: {formatCurrency(order.total_amount || 0)}</span>
                          <span className="font-semibold">Payment: {mapPaymentMethodToDisplay(order.payment_method)}</span>
                          <span className="font-semibold">Option: {formatPaymentOption(order.payment_option)}</span>
                        </div>
                        {order.items && order.items.length > 0 && (
                          <div className="mt-3 sm:mt-4">
                            <p className="text-sm sm:text-base font-semibold text-gray-500 dark:text-gray-400">
                              Items: {order.items.length} product{order.items.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
                        <span className={`px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-bold ${getStatusColor(order.order_status, 'order')}`}>
                          {mapOrderStatusToDisplay(order.order_status)}
                        </span>
                        <span className={`px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-bold ${getStatusColor(order.payment_status, 'payment')}`}>
                  {mapPaymentStatusToDisplay(order.payment_status)}
                        </span>
                        <button 
                          onClick={() => toggleOrderExpansion(order.id)}
                          className="p-2 sm:p-3 text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-xl sm:rounded-2xl transition-all duration-300 touch-manipulation"
                        >
                          {expandedOrders.has(order.id) ? <ChevronUp className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" /> : <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" />}
                        </button>
                        <button 
                          onClick={() => openOrderDetails(order)}
                          className="p-2 sm:p-3 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-xl sm:rounded-2xl transition-all duration-300 touch-manipulation"
                        >
                          <Eye className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" />
                        </button>
                        {/* Show Installments Pay option for EMI/Split payments with confirmed/completed advance payment */}
                        {((order.payment_option === 'emi' || order.payment_option === 'advance') && 
                          (order.payment_status && (order.payment_status.toLowerCase().trim() === 'confirmed' || order.payment_status.toLowerCase().trim() === 'completed'))) && (
                          <button
                            onClick={() => {
                              // Navigate to the EMI/Split Payments section instead of adding to cart
                              setActiveTab('emi-split-payments');
                            }}
                            className="p-2 sm:p-3 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/20 rounded-xl sm:rounded-2xl transition-all duration-300 touch-manipulation"
                            title="Pay Installments"
                          >
                            <CreditCard className="h-4 w-4 sm:h-5 sm:w-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Order Details */}
                  {expandedOrders.has(order.id) && (
                    <div className="border-t-2 border-gray-200 dark:border-gray-700 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-800 dark:to-blue-900/20 backdrop-blur-md">
                      <div className="p-4 sm:p-6 md:p-8">
                        <h4 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">Order Details</h4>
                        
                        {/* Customer Information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 mb-6 sm:mb-8">
                          <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl shadow-lg border border-white/20 p-4 sm:p-6">
                            <h5 className="text-base sm:text-lg font-bold text-gray-700 dark:text-gray-300 mb-3 sm:mb-4">Customer Information</h5>
                            <div className="space-y-2 sm:space-y-3 text-sm sm:text-base text-gray-600 dark:text-gray-400">
                              <p className="flex items-center"><User className="inline h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3" />{order.customer_name}</p>
                              <p className="flex items-center"><Mail className="inline h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3" />{order.customer_email}</p>
                              {order.customer_phone && (
                                <p className="flex items-center"><Phone className="inline h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3" />{order.customer_phone}</p>
                              )}
                            </div>
                          </div>
                          <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl shadow-lg border border-white/20 p-4 sm:p-6">
                            <h5 className="text-base sm:text-lg font-bold text-gray-700 dark:text-gray-300 mb-3 sm:mb-4">Order Information</h5>
                            <div className="space-y-2 sm:space-y-3 text-sm sm:text-base text-gray-600 dark:text-gray-400">
                              <p className="flex items-center"><Clock className="inline h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3" />Created: {formatDate(order.created_at)}</p>
                              <p className="flex items-center"><DollarSign className="inline h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3" />Total: {formatCurrency(order.total_amount || 0)}</p>
                              <p className="flex items-center"><CreditCard className="inline h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3" />Payment: {mapPaymentMethodToDisplay(order.payment_method)}</p>
                              <p className="flex items-center"><Settings className="inline h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3" />Option: {formatPaymentOption(order.payment_option)}</p>
                            </div>
                          </div>
                        </div>

                        {/* Order Items */}
                        {selectedOrder && selectedOrder.items && selectedOrder.items.length > 0 && (
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Order Items</h3>
                            <div className="space-y-6">
                              {selectedOrder.items.map((item, index) => (
                                <div key={index} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                                  {/* Product Header */}
                                  <div className="flex items-center space-x-4 mb-4">
                                    {/* Product Image */}
                                    {item.product_slug ? (
                                      <Link 
                                        to={`/product/${item.product_slug}`}
                                        className="w-20 h-20 rounded-2xl overflow-hidden bg-gradient-to-br from-blue-500 to-purple-600 flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer shadow-lg"
                                      >
                                        {item.product_image ? (
                                          <img 
                                            src={getImageUrl(item.product_image)} 
                                            alt={item.product_name}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                              e.target.nextSibling.style.display = 'flex';
                                            }}
                                          />
                                        ) : null}
                                        <div className={`w-full h-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center ${item.product_image ? 'hidden' : ''}`}>
                                          <Package className="w-10 h-10 text-white" />
                                        </div>
                                      </Link>
                                    ) : (
                                      <div className="w-20 h-20 rounded-2xl overflow-hidden bg-gradient-to-br from-gray-400 to-gray-600 flex-shrink-0 shadow-lg">
                                        {item.product_image ? (
                                          <img 
                                            src={getImageUrl(item.product_image)} 
                                            alt={item.product_name}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                              e.target.nextSibling.style.display = 'flex';
                                            }}
                                          />
                                        ) : null}
                                        <div className={`w-full h-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center ${item.product_image ? 'hidden' : ''}`}>
                                          <Package className="w-10 h-10 text-white" />
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Product Info */}
                                    <div className="flex-1">
                                      {item.product_slug ? (
                                        <Link 
                                          to={`/product/${item.product_slug}`}
                                          className="text-xl font-bold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer"
                                        >
                                          {item.product_name}
                                        </Link>
                                      ) : (
                                        <p className="text-xl font-bold text-gray-900 dark:text-white">{item.product_name}</p>
                                      )}
                                      <p className="text-base font-semibold text-gray-500 dark:text-gray-400 mt-2">Quantity: {item.quantity}</p>
                                    </div>
                                  </div>

                                  {/* Pricing Details */}
                                  <div className="space-y-3">
                                    {/* License Information */}
                                    {item.selected_license && (
                                      <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
                                        <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                                          {item.selected_license === 'extended' ? 'Extended License' : 'Regular License'}
                                        </span>
                                  <div className="text-right">
                                          <span className="font-medium">
                                            {formatCurrency(item.selected_license === 'extended' ? 
                                              (item.license_details?.extended_license_price || item.extended_license_price || 0) :
                                              (item.license_details?.regular_license_price || item.regular_license_price || 0)
                                            )}
                                          </span>
                                          {/* Show original price if different */}
                                          {item.selected_license === 'regular' && item.license_details?.regular_license_original_price && 
                                           item.license_details.regular_license_original_price !== item.license_details.regular_license_price && (
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                              <span className="line-through">
                                                {formatCurrency(item.license_details.regular_license_original_price)}
                                              </span>
                                              {item.license_details.regular_license_discount > 0 && (
                                                <span className="ml-2 text-green-600 dark:text-green-400">{item.license_details.regular_license_discount}% OFF</span>
                                              )}
                                            </div>
                                          )}
                                          {item.selected_license === 'extended' && item.license_details?.extended_license_original_price && 
                                           item.license_details.extended_license_original_price !== item.license_details.extended_license_price && (
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                              <span className="line-through">
                                                {formatCurrency(item.license_details.extended_license_original_price)}
                                              </span>
                                              {item.license_details.extended_license_discount > 0 && (
                                                <span className="ml-2 text-green-600 dark:text-green-400">{item.license_details.extended_license_discount}% OFF</span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Extended Support Information */}
                                    {item.extended_support && (
                                      <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
                                        <span className="text-sm font-medium text-green-600 dark:text-green-400">Extended Support</span>
                                        <div className="text-right">
                                          <span className="font-medium">
                                            {formatCurrency(item.support_details?.extended_support_price || item.extended_support_price || 0)}
                                          </span>
                                          {(item.support_details?.extended_support_original_price || item.extended_support_original_price) && 
                                           (item.support_details?.extended_support_original_price || item.extended_support_original_price) !== (item.support_details?.extended_support_price || item.extended_support_price) && (
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                              <span className="line-through">
                                                {formatCurrency(item.support_details?.extended_support_original_price || item.extended_support_original_price)}
                                              </span>
                                              {(item.support_details?.extended_support_discount || item.extended_support_discount) > 0 && (
                                                <span className="ml-2 text-green-600 dark:text-green-400">{item.support_details?.extended_support_discount || item.extended_support_discount}% OFF</span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Addons Information */}
                                    {item.selected_addons && Array.isArray(item.selected_addons) && item.selected_addons.length > 0 && (
                                      <div className="py-2 border-b border-gray-200 dark:border-gray-600">
                                        <div className="flex justify-between items-center mb-2">
                                          <span className="text-sm font-medium text-purple-600 dark:text-purple-400">Addons</span>
                                        </div>
                                        <div className="space-y-1">
                                          {item.selected_addons.map((addonId, addonIndex) => {
                                            const addon = (item.addon_details || item.addons)?.find(a => a.id === addonId);
                                            return addon ? (
                                              <div key={addonIndex} className="flex justify-between items-center pl-4">
                                                <span className="text-sm text-gray-600 dark:text-gray-400">{addon.name}</span>
                                                <div className="text-right">
                                                  <span className="text-sm font-medium">{formatCurrency(addon.price || 0)}</span>
                                                  {addon.original_price && addon.original_price !== addon.price && (
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                      <span className="line-through">{formatCurrency(addon.original_price)}</span>
                                                      {addon.discount_percentage > 0 && (
                                                        <span className="ml-1 text-green-600 dark:text-green-400">{addon.discount_percentage}% OFF</span>
                                                      )}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            ) : null;
                                          })}
                                        </div>
                                      </div>
                                    )}

                                    {/* Item Total */}
                                    <div className="flex justify-between items-center pt-2">
                                      <span className="text-lg font-semibold text-gray-900 dark:text-white">Total</span>
                                      <span className="text-lg font-semibold text-gray-900 dark:text-white">
                                        {(() => {
                                          // Calculate total including support and addons
                                          let itemTotal = (item.price || 0) * (item.quantity || 1);
                                          
                                          // Add support price if extended support is selected
                                          if (item.extended_support && (item.support_details?.extended_support_price || item.extended_support_price)) {
                                            itemTotal += (item.support_details?.extended_support_price || item.extended_support_price || 0) * (item.quantity || 1);
                                          }
                                          
                                          // Add addon prices if addons are selected
                                          if (item.selected_addons && Array.isArray(item.selected_addons) && item.selected_addons.length > 0 && (item.addon_details || item.addons)) {
                                            item.selected_addons.forEach(addonId => {
                                              const addon = (item.addon_details || item.addons)?.find(a => a.id === addonId);
                                              if (addon && addon.price) {
                                                itemTotal += addon.price * (item.quantity || 1);
                                              }
                                            });
                                          }
                                          
                                          return formatCurrency(itemTotal);
                                        })()}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Billing Address */}
                        {order.billing_address && (
                          <div className="mt-6">
                            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Billing Address</h5>
                            <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {typeof order.billing_address === 'string' 
                                  ? order.billing_address 
                                  : JSON.stringify(order.billing_address, null, 2)
                                }
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Notes */}
                        {order.notes && (
                          <div className="mt-6">
                            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Notes</h5>
                            <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                              <p className="text-sm text-gray-600 dark:text-gray-400">{order.notes}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center shadow-lg">
                <FileText className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">No orders yet</h3>
              <p className="text-lg text-gray-500 dark:text-gray-400 mb-8">
                Your orders will appear here
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className="space-y-8">
      <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Analytics Overview</h2>
      
      {analyticsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20 p-8 animate-pulse">
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-2xl w-3/4 mb-3"></div>
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-2xl w-1/2"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20 p-8 hover:scale-105 transition-all duration-300">
            <div className="flex items-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <div className="ml-6">
                <p className="text-lg font-bold text-gray-600 dark:text-gray-400 mb-1">
                  Total Revenue
                </p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(analytics?.totalRevenue || 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20 p-8 hover:scale-105 transition-all duration-300">
            <div className="flex items-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
              <div className="ml-6">
                <p className="text-lg font-bold text-gray-600 dark:text-gray-400 mb-1">
                  Completed Projects
                </p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {analytics?.completedProjects || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20 p-8 hover:scale-105 transition-all duration-300">
            <div className="flex items-center">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Clock className="h-8 w-8 text-white" />
              </div>
              <div className="ml-6">
                <p className="text-lg font-bold text-gray-600 dark:text-gray-400 mb-1">
                  Active Projects
                </p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {analytics?.activeProjects || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <DollarSign className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Pending Payments
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(analytics?.pendingPayments || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  const renderEMISplitPayments = () => {

    const handleViewPayment = async (payment) => {
      try {
        // Fetch detailed payment transaction data
        const response = await paymentTransactionsAPI.getById(payment.id);
        setSelectedEMISplitPayment(response.data.data);
        setShowEMISplitPaymentModal(true);
      } catch (error) {
        console.error('Error fetching payment details:', error);
        // Fallback to basic payment data
        setSelectedEMISplitPayment(payment);
        setShowEMISplitPaymentModal(true);
      }
    };

    // Helper function to get status display
    const getStatusDisplay = (payment) => {
      if (payment.statusDisplay) {
        return payment.statusDisplay;
      }
      
      // Use effective status if available, otherwise fall back to original status
      const effectiveInstallmentStatus = payment.effectiveInstallmentStatus || payment.installment_status;
      const effectivePaymentStatus = payment.effectivePaymentStatus || payment.payment_status;
      
      // Fallback status display
      const status = effectiveInstallmentStatus || effectivePaymentStatus;
      const dueDate = payment.due_date;
      
      if (!dueDate) {
        return { status: 'coming_soon', label: 'Coming Soon', color: 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-700' };
      }
      
      const now = new Date();
      const due = new Date(dueDate);
      const fiveDaysFromNow = new Date(now.getTime() + (5 * 24 * 60 * 60 * 1000));
      
      switch (status) {
        case 'completed':
          return { status: 'completed', label: 'Completed', color: 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20' };
        case 'overdue':
          return { status: 'overdue', label: 'Overdue', color: 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/20' };
        case 'pending':
          return { status: 'pending', label: 'Pending', color: 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20' };
        case 'coming_soon':
        default:
          if (due <= fiveDaysFromNow) {
            return { status: 'pending', label: 'Pending', color: 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20' };
          }
          return { status: 'coming_soon', label: 'Coming Soon', color: 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20' };
      }
    };

    // Helper function to check if payment should be shown
    const shouldShowPayment = (payment) => {
      if (payment.shouldShowPayment !== undefined) {
        return payment.shouldShowPayment;
      }
      
      // Fallback logic
      const orderStatus = payment.order_status;
      const orderPaymentStatus = payment.order_payment_status;
      const effectiveInstallmentStatus = payment.effectiveInstallmentStatus || payment.installment_status;
      const dueDate = payment.due_date;
      
      // Only show payment if order advance payment is confirmed/completed
      const isOrderAdvanceCompleted = (orderPaymentStatus === 'confirmed' || orderPaymentStatus === 'completed');
      
      if (!isOrderAdvanceCompleted) {
        return false; // Don't show payment if advance payment is not completed
      }
      
      // Show payment if installment is pending or coming soon (only after advance is completed)
      if (effectiveInstallmentStatus === 'pending' || effectiveInstallmentStatus === 'coming_soon') {
        return true;
      }
      
      // Show payment if due within 5 days (only after advance is completed)
      if (dueDate) {
        const now = new Date();
        const due = new Date(dueDate);
        const fiveDaysFromNow = new Date(now.getTime() + (5 * 24 * 60 * 60 * 1000));
        
        if (due <= fiveDaysFromNow) {
          return true;
        }
      }
      
      return false;
    };

    return (
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">EMIs and Split Payments</h2>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => fetchEMISplitPayments()}
              className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow flex items-center space-x-3"
            >
              <RefreshCw className="w-5 h-5" />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20">
          <div className="p-8">
            {/* EMI Summary Section */}
            {emiSplitPayments.length > 0 && (
              <div className="mb-8 card-3d bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 backdrop-blur-md rounded-3xl shadow-xl border border-blue-200 dark:border-blue-800 p-8">
                <h3 className="text-2xl font-bold text-blue-900 dark:text-blue-100 mb-6">
                  EMI & Split Payment Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="text-center card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                    <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                      {emiSplitPayments.length}
                    </div>
                    <div className="text-base font-bold text-blue-700 dark:text-blue-300">
                      Total Payments
                    </div>
                  </div>
                  <div className="text-center card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                    <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">
                      {formatCurrency(emiSplitPayments.reduce((total, payment) => {
                        if (payment.transaction_type === 'split_payment') {
                          // For split payments, calculate total paid amount
                          const remainingAmounts = getRemainingSplitPaymentAmounts(payment);
                          let paidTotal = 0;
                          
                          // Do NOT include advance in Total Paid (per requirement)
                          
                          // Add demo amount if completed
                          const isDemoCompleted = remainingAmounts.afterDemoCompleted || 
                                                 remainingAmounts.afterDemoOrder?.payment_status === 'completed';
                          if (isDemoCompleted) {
                            paidTotal += remainingAmounts.afterDemoAmount;
                          }
                          
                          // Add project amount if completed
                          const isProjectCompleted = remainingAmounts.afterProjectCompleted || 
                                                    remainingAmounts.afterProjectOrder?.payment_status === 'completed';
                          if (isProjectCompleted) {
                            paidTotal += remainingAmounts.afterProjectAmount;
                          }
                          
                          return total + paidTotal;
                        } else {
                          // For EMI payments, use existing logic
                        const effectiveInstallmentStatus = payment.effectiveInstallmentStatus || payment.installment_status;
                        const effectivePaymentStatus = payment.effectivePaymentStatus || payment.payment_status;
                        
                        if (effectiveInstallmentStatus === 'completed' && 
                            effectivePaymentStatus === 'completed' &&
                            payment.installment_number && 
                            payment.installment_number > 0 &&
                            parseFloat(payment.amount || 0) !== parseFloat(payment.order_total || 0)) {
                          return total + parseFloat(payment.amount || 0);
                          }
                        }
                        return total;
                      }, 0))}
                    </div>
                    <div className="text-sm text-green-700 dark:text-green-300">
                      Total Paid
                    </div>
                  </div>
                  <div className="text-center card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                    <div className="text-3xl font-bold text-orange-600 dark:text-orange-400 mb-2">
                      {formatCurrency(emiSplitPayments.reduce((total, payment) => {
                        if (payment.transaction_type === 'split_payment') {
                          // For split payments, calculate remaining amount
                          const remainingAmounts = getRemainingSplitPaymentAmounts(payment);
                          let remainingTotal = 0;
                          
                          // Add demo amount if not completed
                          const isDemoCompleted = remainingAmounts.afterDemoCompleted || 
                                                 remainingAmounts.afterDemoOrder?.payment_status === 'completed';
                          if (!isDemoCompleted) {
                            remainingTotal += remainingAmounts.afterDemoAmount;
                          }
                          
                          // Add project amount if not completed
                          const isProjectCompleted = remainingAmounts.afterProjectCompleted || 
                                                    remainingAmounts.afterProjectOrder?.payment_status === 'completed';
                          if (!isProjectCompleted) {
                            remainingTotal += remainingAmounts.afterProjectAmount;
                          }
                          
                          return total + remainingTotal;
                        } else {
                          // For EMI payments, use existing logic
                        const effectiveInstallmentStatus = payment.effectiveInstallmentStatus || payment.installment_status;
                        const effectivePaymentStatus = payment.effectivePaymentStatus || payment.payment_status;
                        
                        if ((effectiveInstallmentStatus !== 'completed' || effectivePaymentStatus !== 'completed') &&
                            payment.installment_number && 
                            payment.installment_number > 0 &&
                            parseFloat(payment.amount || 0) !== parseFloat(payment.order_total || 0)) {
                          return total + parseFloat(payment.amount || 0);
                          }
                        }
                        return total;
                      }, 0))}
                    </div>
                    <div className="text-base font-bold text-orange-700 dark:text-orange-300">
                      Remaining
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {emiSplitError ? (
              <div className="text-center py-8">
                <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Error loading EMI and Split payments</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4">{emiSplitError}</p>
                <button 
                  onClick={() => fetchEMISplitPayments()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            ) : emiSplitLoading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : emiSplitPayments.length > 0 ? (
              <div className="space-y-4">
                {processedPayments.map((payment) => {
                  const statusDisplay = payment.transaction_type === 'split_payment' ? 
                    (() => {
                      // Custom status display for split payments
                      // First check the actual payment status from the database
                      const actualPaymentStatus = payment.payment_status || payment.effectivePaymentStatus;
                      
                      // Map all available payment statuses from Admin Panel
                      const statusMap = {
                        'pending': { status: 'pending', label: 'Pending', color: 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20' },
                        'completed': { status: 'completed', label: 'Completed', color: 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20' },
                        'failed': { status: 'failed', label: 'Failed', color: 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/20' },
                        'refunded': { status: 'refunded', label: 'Refunded', color: 'text-purple-600 bg-purple-100 dark:text-purple-400 dark:bg-purple-900/20' },
                        'processing': { status: 'processing', label: 'Processing', color: 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20' },
                        'confirmed': { status: 'confirmed', label: 'Confirmed', color: 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20' }
                      };
                      
                      // If we have a specific payment status from the database, use it
                      if (actualPaymentStatus && statusMap[actualPaymentStatus]) {
                        return statusMap[actualPaymentStatus];
                      }
                      
                      // If no specific payment status, fall back to the completion logic
                      const remainingAmounts = getRemainingSplitPaymentAmounts(payment);
                      const advanceStatus = remainingAmounts.isAdvanceCompleted ? 'completed' : 'pending';
                      
                      if (advanceStatus === 'pending') {
                        return { status: 'pending', label: 'Advance Pending', color: 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20' };
                    } else {
                        // Check if any remaining payments are pending
                        const hasPendingPayments = (!remainingAmounts.afterDemoCompleted && !remainingAmounts.afterDemoOrder) || 
                                                  (!remainingAmounts.afterProjectCompleted && !remainingAmounts.afterProjectOrder);
                        
                        if (hasPendingPayments) {
                          return { status: 'pending', label: 'Remaining Pending', color: 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/20' };
                        } else {
                          return { status: 'completed', label: 'Completed', color: 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20' };
                        }
                      }
                    })() : 
                    getStatusDisplay(payment);
                    const showPayment = shouldShowPayment(payment);
                  const remainingAmounts = payment.transaction_type === 'split_payment' ? getRemainingSplitPaymentAmounts(payment) : null;
                                      return (
                  <div key={payment.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {payment.transaction_type === 'emi_payment' ? 'EMI Payment' : 'Split Payment'} #{payment.id}
                          </h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusDisplay.color}`}>
                            {statusDisplay.label}
                          </span>
                          {payment.transaction_type === 'emi_payment' && payment.installment_number && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium text-purple-600 bg-purple-100 dark:text-purple-400 dark:bg-purple-900/20">
                              Installment {payment.effectiveInstallmentNumber || payment.installment_number}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-600 dark:text-gray-400 mb-2">
                          Order #{payment.order_number}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-500 dark:text-gray-400 mb-2">
                          <div>
                            <span className="font-medium">Total Amount:</span> {formatCurrency((() => {
                              // For split payments, try to get the original order total
                              if (payment.transaction_type === 'split_payment') {
                                // First try to get from original_total_amount field
                                if (payment.original_total_amount) {
                                  return payment.original_total_amount;
                                }
                                
                                // If we have order_id, try to find the related order
                                if (payment.order_id && orders.length > 0) {
                                  const relatedOrder = orders.find(order => order.id === payment.order_id);
                                  if (relatedOrder) {
                                    // First check for original_total_amount (this is the correct total)
                                    if (relatedOrder.original_total_amount) {
                                      return relatedOrder.original_total_amount;
                                    }
                                    // Fallback to total_amount
                                    if (relatedOrder.total_amount) {
                                      return relatedOrder.total_amount;
                                    }
                                  }
                                }
                                
                                // Try to find by order_number as fallback
                                if (payment.order_number && orders.length > 0) {
                                  const relatedOrderByNumber = orders.find(order => order.order_number === payment.order_number);
                                  if (relatedOrderByNumber) {
                                    // First check for original_total_amount (this is the correct total)
                                    if (relatedOrderByNumber.original_total_amount) {
                                      return relatedOrderByNumber.original_total_amount;
                                    }
                                    // Fallback to total_amount
                                    if (relatedOrderByNumber.total_amount) {
                                      return relatedOrderByNumber.total_amount;
                                    }
                                  }
                                }
                                
                                // Last resort: use order_total field
                                return payment.order_total;
                              }
                              // For other payment types, use the existing logic
                              return payment.order_total;
                            })())}
                          </div>
                          <div>
                            <span className="font-medium">
                              {payment.order_payment_status === 'confirmed' || payment.order_payment_status === 'completed' 
                                ? 'Advance (Paid):' 
                                : 'Advance (Not Paid):'}
                            </span> 
                            {formatCurrency(payment.amount)}
                          </div>
                          {payment.transaction_type === 'split_payment' ? (
                            // Split Payment specific amounts
                            <>
                              <div>
                                <span className="font-medium text-orange-600 dark:text-orange-400">Remaining Amount:</span> 
                                <span className="text-orange-600 dark:text-orange-400 font-medium">
                                  {(() => {
                                    const remainingAmounts = getRemainingSplitPaymentAmounts(payment);
                                    let remainingTotal = 0;
                                    
                                    // Check if demo payment is actually completed
                                    const isDemoCompleted = remainingAmounts.afterDemoCompleted || 
                                                           remainingAmounts.afterDemoOrder?.payment_status === 'completed';
                                    
                                    // Check if project payment is actually completed
                                    const isProjectCompleted = remainingAmounts.afterProjectCompleted || 
                                                              remainingAmounts.afterProjectOrder?.payment_status === 'completed';
                                    
                                    // Add demo amount if NOT completed
                                    if (!isDemoCompleted) {
                                      remainingTotal += remainingAmounts.afterDemoAmount;
                                    }
                                    
                                    // Add project amount if NOT completed
                                    if (!isProjectCompleted) {
                                      remainingTotal += remainingAmounts.afterProjectAmount;
                                    }
                                    
                                    return formatCurrency(remainingTotal);
                                  })()}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-green-600 dark:text-green-400">Split Paid Amount:</span> 
                                <span className="text-green-600 dark:text-green-400 font-medium">
                                  {(() => {
                                    const remainingAmounts = getRemainingSplitPaymentAmounts(payment);
                                    let paidTotal = 0; // Start with 0, don't include advance amount
                                    
                                    // Check if demo payment is actually completed
                                    const isDemoCompleted = remainingAmounts.afterDemoCompleted || 
                                                           remainingAmounts.afterDemoOrder?.payment_status === 'completed';
                                    
                                    // Check if project payment is actually completed
                                    const isProjectCompleted = remainingAmounts.afterProjectCompleted || 
                                                              remainingAmounts.afterProjectOrder?.payment_status === 'completed';
                                    
                                    // Add demo amount if completed
                                    if (isDemoCompleted) {
                                      paidTotal += remainingAmounts.afterDemoAmount;
                                    }
                                    
                                    // Add project amount if completed
                                    if (isProjectCompleted) {
                                      paidTotal += remainingAmounts.afterProjectAmount;
                                    }
                                    
                                    return formatCurrency(paidTotal);
                                  })()}
                                </span>
                              </div>
                            </>
                          ) : (
                            // EMI Payment amounts (existing logic)
                            <>
                          {payment.remaining_amount && (
                            <div>
                              <span className="font-medium">Remaining:</span> {formatCurrency(payment.remaining_amount)}
                            </div>
                          )}
                          <div>
                            <span className="font-medium text-green-600 dark:text-green-400">
                                  EMI Paid Amount:
                            </span> 
                            <span className="text-green-600 dark:text-green-400 font-medium">
                                  {formatCurrency(payment.emiPaidAmount || 0)}
                            </span>
                        </div>
                            </>
                          )}
                        </div>
                          
                          {/* EMI Progress Bar - Only for EMI Payments */}
                          {payment.transaction_type === 'emi_payment' && payment.total_installments && payment.total_installments > 1 && (
                            <div className="mt-3">
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-xs text-gray-500 dark:text-gray-400">EMI Progress</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {(() => {
                                    // Use the calculated completedInstallments from the dynamic system
                                    const dynamicEMIPayment = emiSplitPayments.find(p => 
                                      p.order_id === payment.order_id &&
                                      p.transaction_type === 'emi_payment' && 
                                      p.total_installments && 
                                      p.total_installments > 1
                                    );
                                    
                                    if (dynamicEMIPayment) {
                                      // Use the installment order status to determine actual progress
                                      const installmentOrderStatus = dynamicEMIPayment.installment_order_payment_status;
                                      let completedInstallments = 0;
                                      
                                      if (installmentOrderStatus === 'completed') {
                                        const effectiveInstallment = dynamicEMIPayment.effectiveInstallmentNumber || dynamicEMIPayment.installment_number || 1;
                                        completedInstallments = Math.max(0, effectiveInstallment - 1);
                                      }
                                      
                                      return `${completedInstallments}/${payment.total_installments} installments`;
                                    } else {
                                      // Fallback to old logic
                                      const completedInstallments = emiSplitPayments.filter(p => {
                                        const effectiveInstallmentStatus = p.effectiveInstallmentStatus || p.installment_status;
                                        const effectivePaymentStatus = p.effectivePaymentStatus || p.payment_status;
                                        const isAdvancePayment = parseFloat(p.amount || 0) === parseFloat(p.order_total || 0);
                                        const isActualInstallment = p.installment_number && p.installment_number > 1;
                                        
                                        return p.order_id === payment.order_id && 
                                               effectiveInstallmentStatus === 'completed' && 
                                               effectivePaymentStatus === 'completed' &&
                                               p.installment_number && 
                                               p.installment_number > 0 &&
                                               !isAdvancePayment &&
                                               isActualInstallment;
                                      }).length;
                                      return `${completedInstallments}/${payment.total_installments} installments`;
                                    }
                                  })()}
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                <div 
                                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                                  style={{
                                    width: `${(() => {
                                      // Use the calculated completedInstallments from the dynamic system
                                      const dynamicEMIPayment = emiSplitPayments.find(p => 
                                        p.order_id === payment.order_id &&
                                        p.transaction_type === 'emi_payment' && 
                                        p.total_installments && 
                                        p.total_installments > 1
                                      );
                                      
                                      if (dynamicEMIPayment) {
                                        // Use the installment order status to determine actual progress
                                        const installmentOrderStatus = dynamicEMIPayment.installment_order_payment_status;
                                        let completedInstallments = 0;
                                        
                                        if (installmentOrderStatus === 'completed') {
                                          const effectiveInstallment = dynamicEMIPayment.effectiveInstallmentNumber || dynamicEMIPayment.installment_number || 1;
                                          completedInstallments = Math.max(0, effectiveInstallment - 1);
                                        }
                                        
                                        return Math.min((completedInstallments / payment.total_installments) * 100, 100);
                                      } else {
                                        // Fallback to old logic
                                        const completedInstallments = emiSplitPayments.filter(p => {
                                          const effectiveInstallmentStatus = p.effectiveInstallmentStatus || p.installment_status;
                                          const effectivePaymentStatus = p.effectivePaymentStatus || p.payment_status;
                                          const isAdvancePayment = parseFloat(p.amount || 0) === parseFloat(p.order_total || 0);
                                          const isActualInstallment = p.installment_number && p.installment_number > 1;
                                          
                                          return p.order_id === payment.order_id && 
                                                 effectiveInstallmentStatus === 'completed' && 
                                                 effectivePaymentStatus === 'completed' &&
                                                 p.installment_number && 
                                                 p.installment_number > 0 &&
                                                 !isAdvancePayment &&
                                                 isActualInstallment;
                                        }).length;
                                        return Math.min((completedInstallments / payment.total_installments) * 100, 100);
                                      }
                                    })()}%`
                                  }}
                                ></div>
                              </div>
                            </div>
                          )}
                          
                          {/* Due Date and Payment Amount - Only for EMI Payments */}
                          {payment.transaction_type === 'emi_payment' && (payment.due_date || payment.next_payment_date) && (
                          <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                            <div className="text-sm text-blue-800 dark:text-blue-200">
                                <div className="flex justify-between items-center mb-1">
                                  <span className="font-medium">Due Date:</span>
                                  <span>{formatDate(payment.effectiveDueDate || payment.due_date || payment.next_payment_date)}</span>
                            </div>
                                <div className="flex justify-between items-center">
                                  <span className="font-medium">Payment Amount:</span>
                                  <span>
                                    {(() => {
                                      // Calculate payment amount based on status
                                      if (payment.installment_status === 'coming_soon' && payment.transaction_type === 'emi_payment') {
                                        // For early payments, show amount without interest
                                        const totalAmount = parseFloat(payment.order_total || 0);
                                        // For EMI payments, the amount field represents the EMI amount with interest
                                        // We need to calculate the principal amount without interest
                                        const emiAmountWithInterest = parseFloat(payment.amount || 0);
                                        const totalInstallments = parseInt(payment.total_installments || 6);
                                        
                                        // Calculate total EMI amount with interest
                                        const totalEmiWithInterest = emiAmountWithInterest * totalInstallments;
                                        
                                        // Calculate principal amount (total without interest)
                                        const principalAmount = totalAmount;
                                        
                                        // Calculate principal per installment (without interest)
                                        const principalPerInstallment = principalAmount / totalInstallments;
                                        const originalAmount = parseFloat(payment.next_payment_amount || payment.amount || 0);
                                        
                                        // Debug logging
                                        console.log('🔍 Early Payment Calculation Debug:', {
                                          paymentId: payment.id,
                                          totalAmount,
                                          emiAmountWithInterest,
                                          totalEmiWithInterest,
                                          principalAmount,
                                          totalInstallments,
                                          principalPerInstallment,
                                          originalAmount,
                                          installment_status: payment.installment_status,
                                          transaction_type: payment.transaction_type
                                        });
                                        
                                        // Ensure we have valid amounts
                                        if (principalPerInstallment > 0 && originalAmount > 0) {
                                          const interestWaiver = originalAmount - principalPerInstallment;
                                          
                                          return (
                                            <div className="text-right">
                                              <div className="text-green-600 dark:text-green-400 font-medium">
                                                {formatCurrency(Math.round(principalPerInstallment * 100) / 100)}
                                              </div>
                                              <div className="text-xs text-gray-500 line-through">
                                                {formatCurrency(originalAmount)}
                                              </div>
                                              <div className="text-xs text-green-600 dark:text-green-400">
                                                ₹{interestWaiver.toFixed(2)} interest waived
                                              </div>
                                            </div>
                                          );
                                        } else {
                                          // Fallback to original amount if calculation fails
                                          console.warn('⚠️ Early payment calculation failed, using original amount:', {
                                            paymentId: payment.id,
                                            principalPerInstallment,
                                            originalAmount
                                          });
                                          return formatCurrency(originalAmount || payment.next_payment_amount || payment.amount || 0);
                                        }
                                      } else {
                                        // For regular payments, show normal amount
                                        return formatCurrency(payment.next_payment_amount || payment.amount);
                                      }
                                    })()}
                                  </span>
                                </div>
                                {payment.interest_waived && payment.interest_waiver_amount > 0 && (
                                  <div className="flex justify-between items-center mt-1 text-green-700 dark:text-green-300">
                                    <span className="font-medium">Interest Waived:</span>
                                    <span>-{formatCurrency(payment.interest_waiver_amount)}</span>
                          </div>
                        )}
                              </div>
                            </div>
                          )}
                          
                          {/* Early Payment Info - Only for EMI Payments */}
                          {payment.transaction_type === 'emi_payment' && payment.early_payment_date && (
                            <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                              <div className="text-xs text-green-700 dark:text-green-300">
                                <span className="font-medium">Early Payment:</span> {formatDate(payment.early_payment_date)}
                              </div>
                            </div>
                          )}
                          
                        {payment.payment_method && (
                          <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                            <span className="font-medium">Payment Method:</span> {mapPaymentMethodToDisplay(payment.payment_method)}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleViewPayment(payment)}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View
                        </button>
                        
                        {/* EMI Payment Options */}
                        {payment.transaction_type === 'emi_payment' && showPayment && payment.next_payment_amount && (
                          <button
                            onClick={() => handleMakePayment(payment)}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <CreditCard className="w-4 h-4 mr-2" />
                              {statusDisplay.status === 'coming_soon' ? 'Pay Early (No Interest)' : 'Pay Now'}
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Split Payment Remaining Amount Options - Positioned Below */}
                    {payment.transaction_type === 'split_payment' && remainingAmounts && remainingAmounts.isAdvanceCompleted && (
                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Remaining Payment Options:</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* After Demo Payment */}
                          {(() => {

                            // Check if payment is completed
                            const isCompleted = (() => {
                              if (remainingAmounts.afterDemoCompleted) return true;
                              if (remainingAmounts.afterDemoOrder?.payment_status === 'completed') return true;
                              const paymentDetails = payment.payment_option_details;
                              if (paymentDetails) {
                                let details = paymentDetails;
                                if (typeof details === 'string') {
                                  try {
                                    details = JSON.parse(details);
                                  } catch (e) {
                                    console.warn('Failed to parse payment option details:', e);
                                  }
                                }
                                if (details.after_demo_order_payment_status === 'completed') return true;
                              }
                              return false;
                            })();

                            // Check if there's an order (completed or pending)
                            const hasOrder = remainingAmounts.afterDemoOrder || remainingAmounts.afterDemoCompleted;

                            if (isCompleted) {
                              // Show completed status
                      return (
                              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                <div className="text-center">
                                  <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                                    {remainingAmounts.afterDemoText} - {formatCurrency(remainingAmounts.afterDemoAmount)}
                                  </span>
                                  <div className="text-xs text-gray-500 mt-1">
                                      Status: Paid
                  </div>
                                </div>
                              </div>
                              );
                            } else if (hasOrder) {
                              // Show pending status with payment button
                              return (
                                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                  <div className="text-center mb-3">
                                    <span className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">
                                      {remainingAmounts.afterDemoText} - {formatCurrency(remainingAmounts.afterDemoAmount)}
                                    </span>
                                    <div className="text-xs text-gray-500 mt-1">
                                      Status: Pending
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handleMakeSplitPayment(payment, 'after_demo', remainingAmounts.afterDemoAmount, remainingAmounts.afterDemoText)}
                                    className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                          >
                            <CreditCard className="w-4 h-4 mr-2" />
                            Pay Now
                                    {remainingAmounts.demoLateFee?.isLate && (
                                      <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                                        +{formatCurrency(remainingAmounts.demoLateFee.lateFee)} late fee
                                      </span>
                                    )}
                          </button>
                                  {remainingAmounts.afterDemoPaymentDate && (
                                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-2 text-center">
                                      <div>Due Date: {formatDate(remainingAmounts.afterDemoPaymentDate)}</div>
                                      <div>
                                        {remainingAmounts.demoLateFee?.isLate ? (
                                          <span className="text-red-600 dark:text-red-400">
                                            Late fee applied: ₹{formatCurrency(remainingAmounts.demoLateFee.lateFee)} ({remainingAmounts.demoLateFee.daysLate} days late)
                                          </span>
                                        ) : (
                                          <span>Late fee: ₹{remainingAmounts.lateFeePerDay}/day starting from 5 days after the due date.</span>
                        )}
                      </div>
                    </div>
                                  )}
                  </div>
                              );
                            } else {
                              // Show payment button
                              return (
                                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                  <div className="text-center mb-3">
                                    <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                                      {remainingAmounts.afterDemoText} - {formatCurrency(remainingAmounts.afterDemoAmount)}
                                    </span>
                                  </div>
                              <button
                                onClick={() => handleMakeSplitPayment(payment, 'after_demo', remainingAmounts.afterDemoAmount, remainingAmounts.afterDemoText)}
                                    className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              >
                                <CreditCard className="w-4 h-4 mr-2" />
                                    Pay Now
                                    {remainingAmounts.demoLateFee?.isLate && (
                                      <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                                        +{formatCurrency(remainingAmounts.demoLateFee.lateFee)} late fee
                                      </span>
                                    )}
                              </button>
                                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-2 text-center">
                                    {remainingAmounts.afterDemoPaymentDate && (
                                      <div>Due Date: {formatDate(remainingAmounts.afterDemoPaymentDate)}</div>
                                    )}
                                    <div>
                                      {remainingAmounts.demoLateFee?.isLate ? (
                                        <span className="text-red-600 dark:text-red-400">
                                          Late fee applied: ₹{formatCurrency(remainingAmounts.demoLateFee.lateFee)} ({remainingAmounts.demoLateFee.daysLate} days late)
                                        </span>
                                      ) : (
                                        <span>Late fee: ₹{remainingAmounts.lateFeePerDay}/day starting from 5 days after the due date.</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                          })()}

                            {/* After Project Payment */}
                          {(() => {


                            // Check if payment is completed
                            const isCompleted = (() => {
                              if (remainingAmounts.afterProjectCompleted) return true;
                              if (remainingAmounts.afterProjectOrder?.payment_status === 'completed') return true;
                              const paymentDetails = payment.payment_option_details;
                              if (paymentDetails) {
                                let details = paymentDetails;
                                if (typeof details === 'string') {
                                  try {
                                    details = JSON.parse(details);
                                  } catch (e) {
                                    console.warn('Failed to parse payment option details:', e);
                                  }
                                }
                                if (details.after_project_order_payment_status === 'completed') return true;
                              }
                              return false;
                            })();

                            // Check if there's an order (completed or pending)
                            const hasOrder = remainingAmounts.afterProjectOrder || remainingAmounts.afterProjectCompleted;

                            if (isCompleted) {
                              // Show completed status
                              return (
                              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                                <div className="text-center">
                                  <span className="text-sm text-purple-600 dark:text-purple-400 font-medium">
                                    {remainingAmounts.afterProjectText} - {formatCurrency(remainingAmounts.afterProjectAmount)}
                                  </span>
                                  <div className="text-xs text-gray-500 mt-1">
                                      Status: Paid
                                  </div>
                                </div>
                              </div>
                              );
                            } else if (hasOrder) {
                              // Show pending status with payment button
                              return (
                                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                  <div className="text-center mb-3">
                                    <span className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">
                                      {remainingAmounts.afterProjectText} - {formatCurrency(remainingAmounts.afterProjectAmount)}
                                    </span>
                                    <div className="text-xs text-gray-500 mt-1">
                                      Status: Pending
                                    </div>
                                  </div>
                              <button
                                onClick={() => handleMakeSplitPayment(payment, 'after_project', remainingAmounts.afterProjectAmount, remainingAmounts.afterProjectText)}
                                    className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                              >
                                <CreditCard className="w-4 h-4 mr-2" />
                                    Pay Now
                                    {remainingAmounts.projectLateFee?.isLate && (
                                      <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                                        +{formatCurrency(remainingAmounts.projectLateFee.lateFee)} late fee
                                      </span>
                                    )}
                              </button>
                                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-2 text-center">
                                    {remainingAmounts.afterProjectPaymentDate && (
                                      <div>Due Date: {formatDate(remainingAmounts.afterProjectPaymentDate)}</div>
                                    )}
                                    <div>
                                      {remainingAmounts.projectLateFee.isLate ? (
                                        <span className="text-red-600 dark:text-red-400">
                                          Late fee applied: ₹{formatCurrency(remainingAmounts.projectLateFee.lateFee)} ({remainingAmounts.projectLateFee.daysLate} days late)
                                        </span>
                                      ) : (
                                        <span>Late fee: ₹{remainingAmounts.lateFeePerDay}/day starting from 5 days after the due date.</span>
                                      )}
                                    </div>
                          </div>
                        </div>
                      );
                            } else {
                              // Show payment button
                              return (
                                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                                  <div className="text-center mb-3">
                                    <span className="text-sm text-purple-600 dark:text-purple-400 font-medium">
                                      {remainingAmounts.afterProjectText} - {formatCurrency(remainingAmounts.afterProjectAmount)}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => handleMakeSplitPayment(payment, 'after_project', remainingAmounts.afterProjectAmount, remainingAmounts.afterProjectText)}
                                    className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                                  >
                                    <CreditCard className="w-4 h-4 mr-2" />
                                    Pay Now
                                    {remainingAmounts.projectLateFee?.isLate && (
                                      <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                                        +{formatCurrency(remainingAmounts.projectLateFee.lateFee)} late fee
                                      </span>
                                    )}
                                  </button>
                                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-2 text-center">
                                    {remainingAmounts.afterProjectPaymentDate && (
                                      <div>Due Date: {formatDate(remainingAmounts.afterProjectPaymentDate)}</div>
                                    )}
                                    <div>
                                      {remainingAmounts.projectLateFee.isLate ? (
                                        <span className="text-red-600 dark:text-red-400">
                                          Late fee applied: ₹{formatCurrency(remainingAmounts.projectLateFee.lateFee)} ({remainingAmounts.projectLateFee.daysLate} days late)
                                        </span>
                                      ) : (
                                        <span>Late fee: ₹{remainingAmounts.lateFeePerDay}/day starting from 5 days after the due date.</span>
                                      )}
                                    </div>
                                  </div>
                  </div>
                );
                            }
            })()}
                        </div>
                      </div>
                    )}
                  </div>
                )})}
              </div>
            ) : (
              <div className="text-center py-8">
                <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No EMI or Split payments found</h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Your EMI and Split payment history will appear here.
                </p>
              </div>
            )}
          </div>
          </div>
        </div>
      );
    };

  const renderInvoices = () => (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Invoices</h2>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 pr-6 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-2xl bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 shadow-lg transition-all duration-300"
            />
          </div>
          {invoicesLoading && (
            <div className="flex items-center text-blue-600 dark:text-blue-400">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 mr-3"></div>
              <span className="font-semibold">Loading...</span>
            </div>
          )}
        </div>
      </div>

      <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20">
        <div className="p-8">
          {invoicesLoading ? (
            <div className="space-y-6">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-2xl w-3/4 mb-3"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-2xl w-1/2"></div>
                </div>
              ))}
            </div>
          ) : (Array.isArray(displayInvoices) && displayInvoices.length > 0) ? (
            <div className="space-y-6">
              {displayInvoices.map((invoice) => (
                <div key={invoice.id} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20 p-8 hover:scale-105 transition-all duration-300">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                        Invoice #{invoice.id}
                      </h3>
                      <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
                        {invoice.description || 'No description available'}
                      </p>
                      <div className="flex items-center space-x-6 text-base text-gray-500 dark:text-gray-400">
                        <span className="font-semibold">Date: {formatDate(invoice.created_at)}</span>
                        <span className="font-semibold">Amount: {formatCurrency(invoice.amount || 0)}</span>
                        <span className="font-semibold">Due: {formatDate(invoice.due_date)}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`px-4 py-2 rounded-full text-sm font-bold ${getStatusColor(invoice.status)}`}>
                        {invoice.status || 'Unknown'}
                      </span>
                      <button className="p-3 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-2xl transition-all duration-300">
                        <Download className="h-6 w-6" />
                      </button>
                      <button 
                        onClick={() => openInvoiceDetails(invoice)}
                        className="p-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-2xl transition-all duration-300"
                      >
                        <Eye className="h-6 w-6" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center shadow-lg">
                <Receipt className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">No invoices yet</h3>
              <p className="text-lg text-gray-500 dark:text-gray-400 mb-8">
                Your invoices will appear here
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderQuotes = () => (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Quotes</h2>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search quotes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 pr-6 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-2xl bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 shadow-lg transition-all duration-300"
            />
          </div>
          {quotesLoading && (
            <div className="flex items-center text-blue-600 dark:text-blue-400">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 mr-3"></div>
              <span className="font-semibold">Loading...</span>
            </div>
          )}
          <button 
            onClick={handleCreateQuote}
            className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow flex items-center space-x-3"
          >
            <Plus className="h-5 w-5" />
            <span>New Quote</span>
          </button>
        </div>
      </div>

      <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20">
        <div className="p-8">
          {quotesLoading ? (
            <div className="space-y-6">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-2xl w-3/4 mb-3"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-2xl w-1/2"></div>
                </div>
              ))}
            </div>
          ) : (Array.isArray(displayQuotes) && displayQuotes.length > 0) ? (
            <div className="space-y-6">
              {displayQuotes.map((quote) => (
                <div key={quote.id} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20 p-8 hover:scale-105 transition-all duration-300">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                        Quote #{quote.id}
                      </h3>
                      <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
                        {quote.description || 'No description available'}
                      </p>
                      <div className="flex items-center space-x-6 text-base text-gray-500 dark:text-gray-400">
                        <span className="font-semibold">Created: {formatDate(quote.created_at)}</span>
                        <span className="font-semibold">Amount: {formatCurrency(quote.amount || 0)}</span>
                        <span className="font-semibold">Valid Until: {formatDate(quote.valid_until)}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`px-4 py-2 rounded-full text-sm font-bold ${getStatusColor(quote.status)}`}>
                        {quote.status || 'Unknown'}
                      </span>
                      <button 
                        onClick={() => openQuoteDetails(quote)}
                        className="p-3 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-2xl transition-all duration-300"
                      >
                        <Eye className="h-6 w-6" />
                      </button>
                      <button className="p-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-2xl transition-all duration-300">
                        <Download className="h-6 w-6" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center shadow-lg">
                <MessageSquare className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">No quotes yet</h3>
              <p className="text-lg text-gray-500 dark:text-gray-400 mb-8">
                Your quotes will appear here
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderEMIs = () => (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white">EMIs & Installments</h2>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search EMIs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 pr-6 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-2xl bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 shadow-lg transition-all duration-300"
            />
          </div>
        </div>
      </div>

      <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20">
        <div className="p-8">
          {emisLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : (Array.isArray(emis) && emis.length > 0) ? (
            <div className="space-y-4">
              {emis.map((emi) => (
                <div key={emi.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        EMI #{emi.id}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-4">
                        {emi.description || 'No description available'}
                      </p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                        <span>Amount: {formatCurrency(emi.amount || 0)}</span>
                        <span>Installments: {emi.installment_number}/{emi.total_installments}</span>
                        <span>Due: {formatDate(emi.due_date)}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(emi.status)}`}>
                        {emi.status || 'Unknown'}
                      </span>
                      <button className="p-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                        <Eye className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No EMIs yet</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                Your EMI and installment details will appear here
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderAppointments = () => (
    <div className="space-y-6 sm:space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <h2 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">Appointments</h2>
        <div className="w-full sm:w-auto">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search appointments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 sm:pl-12 pr-4 sm:pr-6 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 shadow-lg transition-all duration-300"
            />
          </div>
        </div>
      </div>

      <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20">
        <div className="p-4 sm:p-6 md:p-8">
          {appointmentsLoading ? (
            <div className="space-y-6">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-2xl w-3/4 mb-3"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-2xl w-1/2"></div>
                </div>
              ))}
            </div>
          ) : (Array.isArray(displayAppointments) && displayAppointments.length > 0) ? (
            <div className="space-y-4 sm:space-y-6">
              {displayAppointments.map((appointment) => (
                <div key={appointment.id} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-4 sm:p-6 hover:scale-105 transition-all duration-300">
                  <div className="flex items-start sm:items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 truncate">
                        {appointment.purpose || `Appointment #${appointment.id}`}
                      </h3>
                      <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 mb-2 sm:mb-4 line-clamp-2">
                        {appointment.message || 'No additional details'}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 text-sm sm:text-base text-gray-500 dark:text-gray-400">
                        <span className="font-semibold truncate">Name: {appointment.name}</span>
                        <span className="font-semibold truncate">Date: {formatDate(appointment.preferred_date)}</span>
                        <span className="font-semibold truncate">Time: {appointment.preferred_time}</span>
                        <span className="font-semibold truncate">Phone: {appointment.phone}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <span className={`px-2 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-bold ${getStatusColor(appointment.status)}`}>
                        {appointment.status}
                      </span>
                      <button 
                        onClick={() => openAppointmentDetails(appointment)}
                        className="p-2 sm:p-3 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-2xl transition-all duration-300"
                      >
                        <Eye className="h-5 w-5 sm:h-6 sm:w-6" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center shadow-lg">
                <Calendar className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">No appointments yet</h3>
              <p className="text-lg text-gray-500 dark:text-gray-400 mb-8">
                Your appointments will appear here
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
  const renderFiles = () => (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Files & Documents</h2>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search files..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 pr-6 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-2xl bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 shadow-lg transition-all duration-300"
            />
          </div>
        </div>
      </div>

      <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20">
        <div className="p-8">
          {filesLoading ? (
            <div className="space-y-6">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-2xl w-3/4 mb-3"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-2xl w-1/2"></div>
                </div>
              ))}
            </div>
          ) : (Array.isArray(files) && files.length > 0) ? (
            <div className="space-y-6">
              {files.map((file) => (
                <div key={file.id} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20 p-8 hover:scale-105 transition-all duration-300">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                        {file.title || file.filename}
                      </h3>
                      <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
                        {file.description || 'No description available'}
                      </p>
                      <div className="flex items-center space-x-6 text-base text-gray-500 dark:text-gray-400">
                        <span className="font-semibold">Type: {file.file_type}</span>
                        <span className="font-semibold">Size: {file.file_size}</span>
                        <span className="font-semibold">Uploaded: {formatDate(file.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <button className="p-3 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-2xl transition-all duration-300">
                        <Download className="h-6 w-6" />
                      </button>
                      <button className="p-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-2xl transition-all duration-300">
                        <Eye className="h-6 w-6" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center shadow-lg">
                <Folder className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">No files yet</h3>
              <p className="text-lg text-gray-500 dark:text-gray-400 mb-8">
                Your files and documents will appear here
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Helper function to render payment details
  const renderPaymentDetails = (details) => {
    // Handle the complex payment option details structure
    const emiDetails = details.emi_details || {};
    const paymentOptionsConfig = details.paymentOptionsConfig || {};
    const selectedPaymentOption = details.selectedPaymentOption;
    
    return (
      <div className="space-y-3">
        {/* Payment Option Type */}
        {selectedPaymentOption && (
          <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Payment Type</span>
            <span className="text-sm text-gray-900 dark:text-white">
              {selectedPaymentOption === 'emi' ? 'EMI Payment' : 
               selectedPaymentOption === 'advance' ? 'Split Payment' : 
               selectedPaymentOption === 'full' ? 'Full Payment' : 
               selectedPaymentOption}
            </span>
          </div>
        )}

        {/* EMI Details */}
        {emiDetails.selectedEmiTenure && (
          <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">EMI Tenure</span>
            <span className="text-sm text-gray-900 dark:text-white">{emiDetails.selectedEmiTenure} months</span>
          </div>
        )}
        
        {emiDetails.emiAmount && (
          <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Monthly EMI Amount</span>
            <span className="text-sm text-gray-900 dark:text-white">{formatCurrency(emiDetails.emiAmount)}</span>
          </div>
        )}
        
        {emiDetails.totalEmiAmount && (
          <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total EMI Amount</span>
            <span className="text-sm text-gray-900 dark:text-white">{formatCurrency(emiDetails.totalEmiAmount)}</span>
          </div>
        )}
        
        {emiDetails.emiPrincipal && (
          <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">EMI Principal</span>
            <span className="text-sm text-gray-900 dark:text-white">{formatCurrency(emiDetails.emiPrincipal)}</span>
          </div>
        )}
        
        {emiDetails.emiAdvanceAmount && (
          <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Advance Amount</span>
            <span className="text-sm text-gray-900 dark:text-white">{formatCurrency(emiDetails.emiAdvanceAmount)}</span>
          </div>
        )}

        {/* Payment Options Configuration */}
        {paymentOptionsConfig.emi_interest_rate && (
          <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Interest Rate</span>
            <span className="text-sm text-gray-900 dark:text-white">{paymentOptionsConfig.emi_interest_rate}% p.a.</span>
          </div>
        )}
        
        {paymentOptionsConfig.emi_processing_fee !== undefined && (
          <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Processing Fee</span>
            <span className="text-sm text-gray-900 dark:text-white">
              {paymentOptionsConfig.emi_processing_fee > 0 ? formatCurrency(paymentOptionsConfig.emi_processing_fee) : 'No Fee'}
            </span>
          </div>
        )}

        {/* Applied Coupon */}
        {details.appliedCoupon && (
          <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Applied Coupon</span>
            <span className="text-sm text-gray-900 dark:text-white">{details.appliedCoupon}</span>
          </div>
        )}
      </div>
    );
  };

  // Prepare processed payments with computed EMI paid amounts per order
  const processedPayments = (() => {
    try {
      const paymentsByOrder = {};
      emiSplitPayments.forEach((payment) => {
        if (!paymentsByOrder[payment.order_id]) {
          paymentsByOrder[payment.order_id] = [];
        }
        paymentsByOrder[payment.order_id].push(payment);
      });

      Object.keys(paymentsByOrder).forEach((orderId) => {
        const orderPayments = paymentsByOrder[orderId];
        let emiPaidAmount = 0;
        let completedInstallments = 0;

        const dynamicEMIPayment = orderPayments.find((p) =>
          p.transaction_type === 'emi_payment' && p.total_installments && p.total_installments > 1
        );

        if (dynamicEMIPayment) {
          const installmentOrderStatus = dynamicEMIPayment.installment_order_payment_status;
          const dbInstallmentNumber = dynamicEMIPayment.installment_number || 1;
          const totalInstallments = dynamicEMIPayment.total_installments || 6;
          const emiAmount = parseFloat(dynamicEMIPayment.next_payment_amount || dynamicEMIPayment.amount || 0);

          let effectiveInstallmentNumber = dbInstallmentNumber;
          if (installmentOrderStatus === 'completed') {
            effectiveInstallmentNumber = Math.min(dbInstallmentNumber + 1, totalInstallments);
            completedInstallments = Math.max(0, effectiveInstallmentNumber - 1);
            emiPaidAmount = completedInstallments * emiAmount;
          } else {
            completedInstallments = 0;
            emiPaidAmount = 0;
          }
        } else {
          const completedPayments = orderPayments.filter((p) => {
            const effectiveInstallmentStatus = p.effectiveInstallmentStatus || p.installment_status;
            const effectivePaymentStatus = p.effectivePaymentStatus || p.payment_status;
            const isAdvancePayment = parseFloat(p.amount || 0) === parseFloat(p.order_total || 0);
            const isActualInstallment = p.installment_number && p.installment_number > 1;
            return (
              effectiveInstallmentStatus === 'completed' &&
              effectivePaymentStatus === 'completed' &&
              p.installment_number &&
              p.installment_number > 0 &&
              !isAdvancePayment &&
              isActualInstallment
            );
          });
          emiPaidAmount = completedPayments.reduce((total, p) => total + parseFloat(p.amount || 0), 0);
          completedInstallments = completedPayments.length;
        }

        orderPayments.forEach((p) => {
          p.emiPaidAmount = emiPaidAmount;
        });
      });

      return emiSplitPayments.map((p) => ({ ...p }));
    } catch (_) {
      return emiSplitPayments;
    }
  })();

  const renderProfile = () => {
    const handleSave = async () => {
      try {
        // Use the updateProfile function from AuthContext
        const result = await updateProfile(profileData);
        
        if (result.success) {
          setProfileUpdateSuccess(true);
          setIsEditingProfile(false);
          setTimeout(() => setProfileUpdateSuccess(false), 3000);
        } else {
          setProfileUpdateError(result.message || 'Failed to update profile');
          setTimeout(() => setProfileUpdateError(''), 3000);
        }
      } catch (error) {
        setProfileUpdateError('Failed to update profile');
        setTimeout(() => setProfileUpdateError(''), 3000);
      }
    };

    return (
      <div className="space-y-8">
        {/* Profile Header */}
        <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20 p-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Profile Information</h2>
            <button
              onClick={() => setIsEditingProfile(!isEditingProfile)}
              className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow flex items-center space-x-3"
            >
              <Edit className="h-5 w-5" />
              <span>{isEditingProfile ? 'Cancel' : 'Edit Profile'}</span>
            </button>
          </div>

          {/* Success/Error Messages */}
          {profileUpdateSuccess && (
            <div className="mb-6 card-3d bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-green-200 dark:border-green-800 p-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <span className="text-lg font-bold text-green-800 dark:text-green-200">Profile updated successfully!</span>
              </div>
            </div>
          )}

          {profileUpdateError && (
            <div className="mb-6 card-3d bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-red-200 dark:border-red-800 p-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center shadow-lg">
                  <AlertCircle className="w-5 h-5 text-white" />
                </div>
                <span className="text-lg font-bold text-red-800 dark:text-red-200">{profileUpdateError}</span>
              </div>
            </div>
          )}

          {/* Profile Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <label className="block text-lg font-bold text-gray-700 dark:text-gray-300 mb-3">
                Full Name
              </label>
              {isEditingProfile ? (
                <input
                  type="text"
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  className="w-full px-4 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white shadow-lg transition-all duration-300"
                />
              ) : (
                <p className="text-lg text-gray-900 dark:text-white font-semibold">{profileData.name || 'Not provided'}</p>
              )}
            </div>

            <div>
              <label className="block text-lg font-bold text-gray-700 dark:text-gray-300 mb-3">
                Email Address
              </label>
              {isEditingProfile ? (
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  className="w-full px-4 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white shadow-lg transition-all duration-300"
                />
              ) : (
                <p className="text-lg text-gray-900 dark:text-white font-semibold">{profileData.email || 'Not provided'}</p>
              )}
            </div>

            <div>
              <label className="block text-lg font-bold text-gray-700 dark:text-gray-300 mb-3">
                Phone Number
              </label>
              {isEditingProfile ? (
                <input
                  type="tel"
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  className="w-full px-4 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white shadow-lg transition-all duration-300"
                />
              ) : (
                <p className="text-lg text-gray-900 dark:text-white font-semibold">{profileData.phone || 'Not provided'}</p>
              )}
            </div>

            <div>
              <label className="block text-lg font-bold text-gray-700 dark:text-gray-300 mb-3">
                WhatsApp Number
              </label>
              {isEditingProfile ? (
                <input
                  type="tel"
                  value={profileData.whatsapp}
                  onChange={(e) => setProfileData({ ...profileData, whatsapp: e.target.value })}
                  placeholder="+91-9876543210"
                  className="w-full px-4 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white shadow-lg transition-all duration-300"
                />
              ) : (
                <p className="text-lg text-gray-900 dark:text-white font-semibold">{profileData.whatsapp || 'Not provided'}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-lg font-bold text-gray-700 dark:text-gray-300 mb-3">
                Address
              </label>
              {isEditingProfile ? (
                <textarea
                  value={profileData.address}
                  onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                  rows={3}
                  placeholder="Enter your complete address"
                  className="w-full px-4 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white shadow-lg transition-all duration-300"
                />
              ) : (
                <p className="text-lg text-gray-900 dark:text-white font-semibold">{profileData.address || 'Not provided'}</p>
              )}
            </div>
          </div>

          {/* Save Button */}
          {isEditingProfile && (
            <div className="mt-8 flex justify-end">
              <button
                onClick={handleSave}
                className="card-3d bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-4 rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow flex items-center gap-3"
              >
                <Save className="h-5 w-5" />
                Save Changes
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSection = (sectionId) => {
    switch (sectionId) {
      case 'overview':
        return renderOverview();
      case 'orders':
        return renderOrders();
      case 'emi-split-payments':
        return renderEMISplitPayments();
      case 'appointments':
        return renderAppointments();
      case 'profile':
        return renderProfile();
      case 'console':
        return <MyConsole />;
      case 'coupons':
        return <UserCoupons />;
      default:
        return (
          <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20 p-12">
            <div className="text-center">
              <div className="w-24 h-24 mx-auto mb-8 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center shadow-lg">
                <Monitor className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {dashboardSections.find(s => s.id === sectionId)?.label} Section
              </h3>
              <p className="text-lg text-gray-500 dark:text-gray-400">
                This section is under development. Coming soon!
              </p>
            </div>
          </div>
        );
    }
  };

  // Get the SpecialDayBanner component from window object
  const [SpecialDayBanner, setSpecialDayBanner] = useState(null);
  
  useEffect(() => {
    const checkForBanner = () => {
      if (typeof window !== 'undefined' && window.SpecialDayBanner) {
        console.log('🎯 UserDashboard: Found SpecialDayBanner component');
        setSpecialDayBanner(() => window.SpecialDayBanner);
      }
    };

    checkForBanner();
    
    // Check periodically in case the component is loaded later
    const interval = setInterval(checkForBanner, 100);
    
    return () => clearInterval(interval);
  }, []);
  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900 relative">
      {/* Special Day Banner - rendered before header */}
      {SpecialDayBanner && (
        <>
          {console.log('🎯 UserDashboard: Rendering SpecialDayBanner')}
          <SpecialDayBanner />
        </>
      )}
      <Header />
      
      {/* Success Message */}
      {successMessage && (
        <div className="card-3d bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 backdrop-blur-md border border-green-200 dark:border-green-700 relative z-10 shadow-xl" style={{
          marginTop: SpecialDayBanner ? '40px' : '0px',
          transition: 'margin-top 0.3s ease'
        }}>
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="flex items-center justify-between py-6">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                  <CheckCircle className="h-5 w-5 text-white" />
                </div>
                <p className="text-green-800 dark:text-green-200 font-bold text-lg">{successMessage}</p>
              </div>
              <button
                onClick={() => setSuccessMessage(null)}
                className="p-2 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 hover:bg-green-100 dark:hover:bg-green-900/20 rounded-full transition-all duration-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Dashboard Header */}
      <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md shadow-xl border-b border-gray-200 dark:border-gray-700 relative z-10" style={{
        marginTop: SpecialDayBanner ? '40px' : '0px',
        transition: 'margin-top 0.3s ease'
      }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex justify-between items-center py-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                Welcome back, {user?.name}!
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Manage your projects, orders, and more
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {/* Bell icon hidden */}
              <Link
                to="/profile"
                className="p-3 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/80 dark:hover:bg-white/10 backdrop-blur-md rounded-2xl transition-all duration-300 shadow-lg"
              >
                <Settings className="h-6 w-6" />
              </Link>
              <button
                onClick={handleLogout}
                className="p-3 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/80 dark:hover:bg-white/10 backdrop-blur-md rounded-2xl transition-all duration-300 shadow-lg"
              >
                <LogOut className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-10xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 py-6 sm:py-8 md:py-12 relative z-0 overflow-hidden">
        {/* Navigation Tabs */}
        <div className="mb-6 sm:mb-8 md:mb-10 flex-shrink-0">
          <nav className="flex space-x-2 sm:space-x-4 md:space-x-6 overflow-x-auto scrollbar-hide h-14 sm:h-16 items-center">
            {dashboardSections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveTab(section.id)}
                  className={`flex items-center space-x-2 sm:space-x-3 px-3 sm:px-4 md:px-6 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-bold text-sm sm:text-base transition-all duration-300 shadow-lg flex-shrink-0 touch-manipulation whitespace-nowrap ${
                    activeTab === section.id
                      ? 'card-3d bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-glow'
                      : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300 bg-white/80 dark:bg-white/10 backdrop-blur-md hover:scale-105'
                  }`}
                >
                  <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
                  <span className="hidden sm:inline">{section.label}</span>
                  <span className="sm:hidden text-xs">{section.shortLabel || section.label.split(' ')[0]}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="overflow-hidden">
        {renderSection(activeTab)}
        </div>
      </div>

      {/* EMI/Split Payment Details Modal */}
      {showEMISplitPaymentModal && selectedEMISplitPayment && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[60] p-2 sm:p-4 md:p-6">
          <div className="card-3d bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl border border-white/30 w-full max-w-7xl h-[95vh] overflow-hidden flex flex-col">
            <div className="p-4 sm:p-6 md:p-8 border-b-2 border-gray-200 dark:border-gray-700 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/10 dark:to-amber-900/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                    <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white truncate">
                      {selectedEMISplitPayment.transaction_type === 'emi_payment' ? 'EMI Payment' : 'Split Payment'} Details
                </h2>
                    <p className="text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-400 font-semibold truncate">
                      #{selectedEMISplitPayment.id}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 sm:space-x-4 ml-2">
                  <button
                    onClick={() => setShowEMISplitPaymentModal(false)}
                    className="p-2 sm:p-3 md:p-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-xl sm:rounded-2xl transition-all duration-300 touch-manipulation"
                  >
                    <X className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-8 space-y-8 overflow-y-auto flex-1">
              {/* Payment Summary */}
              <div className="card-3d bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 backdrop-blur-md rounded-3xl shadow-lg border border-orange-200 dark:border-orange-800 p-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Activity className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Order Status</p>
                    <p className={`text-2xl font-bold ${getStatusColor(selectedEMISplitPayment.order_status, 'order')}`}>
                    {mapOrderStatusToDisplay(selectedEMISplitPayment.order_status)}
                  </p>
                </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <CreditCard className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Payment Status</p>
                    <p className={`text-2xl font-bold ${getStatusColor(selectedEMISplitPayment.payment_status, 'payment')}`}>
                    {mapPaymentStatusToDisplay(selectedEMISplitPayment.payment_status)}
                  </p>
                </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <DollarSign className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Total Amount</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency((() => {
                      // For split payments, try to get the original order total
                      if (selectedEMISplitPayment.transaction_type === 'split_payment') {

                        
                        // First try to get from original_total_amount field
                        if (selectedEMISplitPayment.original_total_amount) {
                          return selectedEMISplitPayment.original_total_amount;
                        }
                        
                        // If we have order_id, try to find the related order (this should be the primary source)
                        if (selectedEMISplitPayment.order_id && orders.length > 0) {
                          const relatedOrder = orders.find(order => order.id === selectedEMISplitPayment.order_id);
                          if (relatedOrder) {
                            // First check for original_total_amount (this is the correct total)
                            if (relatedOrder.original_total_amount) {
                              return relatedOrder.original_total_amount;
                            }
                            // Fallback to total_amount
                            if (relatedOrder.total_amount) {
                              return relatedOrder.total_amount;
                            }
                          }
                        }
                        
                        // Try to find by order_number as fallback
                        if (selectedEMISplitPayment.order_number && orders.length > 0) {
                          const relatedOrderByNumber = orders.find(order => order.order_number === selectedEMISplitPayment.order_number);
                          if (relatedOrderByNumber) {
                            // First check for original_total_amount (this is the correct total)
                            if (relatedOrderByNumber.original_total_amount) {
                              return relatedOrderByNumber.original_total_amount;
                            }
                            // Fallback to total_amount
                            if (relatedOrderByNumber.total_amount) {
                              return relatedOrderByNumber.total_amount;
                            }
                          }
                        }
                        
                        // Last resort: use order_total field (but this might be the payment amount)
                        if (selectedEMISplitPayment.order_total) {
                          return selectedEMISplitPayment.order_total;
                        }
                        
                        // Fallback to amount field
                        return selectedEMISplitPayment.amount;
                      }
                      // For other payment types, use the existing logic
                      return selectedEMISplitPayment.order_total || selectedEMISplitPayment.original_total_amount || selectedEMISplitPayment.amount;
                    })())}
                  </p>
                  </div>
                </div>
              </div>

              {/* Payment Option Details */}
              {selectedEMISplitPayment.payment_option && selectedEMISplitPayment.payment_option !== 'full' && (
                <div className="card-3d bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 backdrop-blur-md rounded-3xl shadow-lg border border-blue-200 dark:border-blue-800 p-8">
                  <h4 className="text-lg font-bold text-blue-800 dark:text-blue-200 mb-6">Payment Option Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Payment Type</p>
                      <p className={`text-lg font-semibold ${
                        selectedEMISplitPayment.payment_option === 'advance' 
                          ? 'text-blue-600 dark:text-blue-400' 
                          : selectedEMISplitPayment.payment_option === 'emi'
                          ? 'text-purple-600 dark:text-purple-400'
                          : 'text-green-600 dark:text-green-400'
                      }`}>
                        {selectedEMISplitPayment.payment_option === 'advance' ? 'Split Payment' : 
                         selectedEMISplitPayment.payment_option === 'emi' ? 'EMI Payment' : 'Full Payment'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Payment Method</p>
                      <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                        {mapPaymentMethodToDisplay(selectedEMISplitPayment.payment_method)}
                      </p>
                    </div>
                    {selectedEMISplitPayment.next_payment_amount && (
                      <div>
                        <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Payment Amount</p>
                        <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                          {formatCurrency(selectedEMISplitPayment.next_payment_amount)}
                        </p>
                        {selectedEMISplitPayment.interest_waived && selectedEMISplitPayment.interest_waiver_amount > 0 && (
                          <div className="mt-1">
                            <p className="text-sm font-medium text-green-700 dark:text-green-300">Interest Waived</p>
                            <p className="text-lg font-semibold text-green-900 dark:text-green-100">
                              -{formatCurrency(selectedEMISplitPayment.interest_waiver_amount)}
                        </p>
                      </div>
                    )}
                      </div>
                    )}
                    {(selectedEMISplitPayment.due_date || selectedEMISplitPayment.next_payment_date) && (
                      <div>
                        <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Due Date</p>
                        <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                          {formatDate(selectedEMISplitPayment.effectiveDueDate || selectedEMISplitPayment.due_date || selectedEMISplitPayment.next_payment_date)}
                        </p>
                      </div>
                    )}
                    {selectedEMISplitPayment.transaction_type === 'split_payment' && (
                      <div>
                        <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Advance Amount</p>
                        <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                          {formatCurrency(selectedEMISplitPayment.amount)}
                        </p>
                      </div>
                    )}
                    {selectedEMISplitPayment.remaining_amount && (
                      <div>
                        <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Remaining Amount</p>
                        <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                          {formatCurrency(selectedEMISplitPayment.remaining_amount)}
                        </p>
                      </div>
                    )}
                    {selectedEMISplitPayment.total_installments && (
                      <div>
                        <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Total Installments</p>
                        <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                          {selectedEMISplitPayment.total_installments}
                        </p>
                      </div>
                    )}
                    {selectedEMISplitPayment.current_installment && selectedEMISplitPayment.transaction_type === 'emi_payment' && (
                      <div>
                        <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Current Installment</p>
                        <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                          {selectedEMISplitPayment.current_installment}
                        </p>
                      </div>
                    )}
                  </div>
                  

                  
                  {selectedEMISplitPayment.payment_option === 'emi' && (
                    <div className="mt-6 card-3d bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-purple-200 dark:border-purple-800 p-6">
                      <p className="text-base font-bold text-purple-800 dark:text-purple-200 mb-4">
                        Your EMI payment plan details are shown below. Please ensure timely payments.
                      </p>
                      
                      {/* EMI Details */}
                      {selectedEMISplitPayment.payment_option_details && (
                        <div className="mt-3 space-y-2">
                          {(() => {
                            const details = selectedEMISplitPayment.payment_option_details;
                            
                            // Handle string data (try to parse JSON)
                            if (typeof details === 'string') {
                              try {
                                const parsedDetails = JSON.parse(details);
                                const emiDetails = parsedDetails.emi_details || {};
                                return (
                                  <div className="space-y-2">
                                    {emiDetails.selectedEmiTenure && (
                                      <div className="flex justify-between text-sm">
                                        <span className="text-purple-700 dark:text-purple-300">EMI Tenure:</span>
                                        <span className="text-purple-900 dark:text-purple-100 font-medium">
                                          {emiDetails.selectedEmiTenure} months
                                        </span>
                                      </div>
                                    )}
                                    {emiDetails.emiAmount && (
                                      <div className="flex justify-between text-sm">
                                        <span className="text-purple-700 dark:text-purple-300">Monthly EMI:</span>
                                        <span className="text-purple-900 dark:text-purple-100 font-medium">
                                          {formatCurrency(emiDetails.emiAmount)}
                                        </span>
                                      </div>
                                    )}
                                    {emiDetails.emiPrincipal && (
                                      <div className="flex justify-between text-sm">
                                        <span className="text-purple-700 dark:text-purple-300">EMI Principal:</span>
                                        <span className="text-purple-900 dark:text-purple-100 font-medium">
                                          {formatCurrency(emiDetails.emiPrincipal)}
                                        </span>
                                      </div>
                                    )}
                                    {emiDetails.totalEmiAmount && (
                                      <div className="flex justify-between text-sm">
                                        <span className="text-purple-700 dark:text-purple-300">Total EMI Amount:</span>
                                        <span className="text-purple-900 dark:text-purple-100 font-medium">
                                          {formatCurrency(emiDetails.totalEmiAmount)}
                                        </span>
                                      </div>
                                    )}
                                    {parsedDetails.paymentOptionsConfig?.emi_interest_rate && (
                                      <div className="flex justify-between text-sm">
                                        <span className="text-purple-700 dark:text-purple-300">Interest Rate:</span>
                                        <span className="text-purple-900 dark:text-purple-100 font-medium">
                                          {parsedDetails.paymentOptionsConfig.emi_interest_rate}% p.a.
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                );
                              } catch (e) {
                                return <div className="text-purple-700 dark:text-purple-300 text-sm">EMI details not available</div>;
                              }
                            }
                            
                            // Handle object data
                            if (typeof details === 'object' && details !== null) {
                              const emiDetails = details.emi_details || {};
                              return (
                                <div className="space-y-2">
                                  {emiDetails.selectedEmiTenure && (
                                    <div className="flex justify-between text-sm">
                                      <span className="text-purple-700 dark:text-purple-300">EMI Tenure:</span>
                                      <span className="text-purple-900 dark:text-purple-100 font-medium">
                                        {emiDetails.selectedEmiTenure} months
                                      </span>
                                    </div>
                                  )}
                                  {emiDetails.emiAmount && (
                                    <div className="flex justify-between text-sm">
                                      <span className="text-purple-700 dark:text-purple-300">Monthly EMI:</span>
                                      <span className="text-purple-900 dark:text-purple-100 font-medium">
                                        {formatCurrency(emiDetails.emiAmount)}
                                      </span>
                                    </div>
                                  )}
                                  {emiDetails.emiPrincipal && (
                                    <div className="flex justify-between text-sm">
                                      <span className="text-purple-700 dark:text-purple-300">EMI Principal:</span>
                                      <span className="text-purple-900 dark:text-purple-100 font-medium">
                                        {formatCurrency(emiDetails.emiPrincipal)}
                                      </span>
                                    </div>
                                  )}
                                  {emiDetails.totalEmiAmount && (
                                    <div className="flex justify-between text-sm">
                                      <span className="text-purple-700 dark:text-purple-300">Total EMI Amount:</span>
                                      <span className="text-purple-900 dark:text-purple-100 font-medium">
                                        {formatCurrency(emiDetails.totalEmiAmount)}
                                      </span>
                                    </div>
                                  )}
                                  {details.paymentOptionsConfig?.emi_interest_rate && (
                                    <div className="flex justify-between text-sm">
                                      <span className="text-purple-700 dark:text-purple-300">Interest Rate:</span>
                                      <span className="text-purple-900 dark:text-purple-100 font-medium">
                                        {details.paymentOptionsConfig.emi_interest_rate}% p.a.
                                      </span>
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            
                            return <div className="text-purple-700 dark:text-purple-300 text-sm">EMI details not available</div>;
                          })()}
                        </div>
                      )}
                    </div>
                  )}

                  {selectedEMISplitPayment.payment_option === 'advance' && (
                    <div className="mt-6 card-3d bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-blue-200 dark:border-blue-800 p-6">
                      <p className="text-base font-bold text-blue-800 dark:text-blue-200 mb-4">
                        Your Split Payment plan details are shown below.
                      </p>
                      {(() => {
                        const details = selectedEMISplitPayment.payment_option_details;
                        // Use current payment configuration instead of stored configuration
                        let cfg = currentPaymentConfig || {};
                        
                        // Calculate the correct total amount for split payments
                        let totalAmount = 0;
                        if (selectedEMISplitPayment.transaction_type === 'split_payment') {
                          // First try to get from original_total_amount field
                          if (selectedEMISplitPayment.original_total_amount) {
                            totalAmount = parseFloat(selectedEMISplitPayment.original_total_amount);
                          }
                          // If we have order_id, try to find the related order
                          else if (selectedEMISplitPayment.order_id && orders.length > 0) {
                            const relatedOrder = orders.find(order => order.id === selectedEMISplitPayment.order_id);
                            if (relatedOrder) {
                              // First check for original_total_amount (this is the correct total)
                              if (relatedOrder.original_total_amount) {
                                totalAmount = parseFloat(relatedOrder.original_total_amount);
                              }
                              // Fallback to total_amount
                              else if (relatedOrder.total_amount) {
                                totalAmount = parseFloat(relatedOrder.total_amount);
                              }
                            }
                          }
                          // Try to find by order_number as fallback
                          else if (selectedEMISplitPayment.order_number && orders.length > 0) {
                            const relatedOrderByNumber = orders.find(order => order.order_number === selectedEMISplitPayment.order_number);
                            if (relatedOrderByNumber) {
                              // First check for original_total_amount (this is the correct total)
                              if (relatedOrderByNumber.original_total_amount) {
                                totalAmount = parseFloat(relatedOrderByNumber.original_total_amount);
                              }
                              // Fallback to total_amount
                              else if (relatedOrderByNumber.total_amount) {
                                totalAmount = parseFloat(relatedOrderByNumber.total_amount);
                              }
                            }
                          }
                          // Last resort: use order_total field
                          else {
                            totalAmount = parseFloat(selectedEMISplitPayment.order_total || selectedEMISplitPayment.amount || 0);
                          }
                        } else {
                          // For other payment types, use the existing logic
                          totalAmount = parseFloat(selectedEMISplitPayment.order_total || selectedEMISplitPayment.amount || 0);
                        }
                        
                        // Fallback to stored configuration if current config is not available
                        if (!currentPaymentConfig) {
                        // Handle string data (try to parse JSON)
                        if (typeof details === 'string') {
                          try {
                            const parsedDetails = JSON.parse(details);
                            cfg = parsedDetails.paymentOptionsConfig || {};
                          } catch (e) {
                            console.warn('Failed to parse payment option details:', e);
                          }
                        }
                        
                        // Handle object data
                        if (typeof details === 'object' && details !== null) {
                          cfg = details.paymentOptionsConfig || {};
                          }
                        }
                        
                        const pNow = parseFloat(cfg.advance_payment_percentage || 50);
                        const pAfterDemo = parseFloat(cfg.advance_payment_after_demo_percentage || 25);
                        const pAfterProject = parseFloat(cfg.advance_payment_after_project_percentage || 25);
                        const aNow = (totalAmount * pNow) / 100;
                        const aAfterDemo = (totalAmount * pAfterDemo) / 100;
                        const aAfterProject = (totalAmount * pAfterProject) / 100;
                        

                        
                        return (
                          <div className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
                            <div className="flex justify-between">
                              <span>{cfg.advance_payment_now_text || 'Split (Now)'}:</span>
                              <span className="font-medium">{formatCurrency(aNow)} ({pNow}%)</span>
                            </div>
                            <div className="flex justify-between">
                              <span>{cfg.advance_payment_after_demo_text || 'After Demo Completion'}:</span>
                              <span className="font-medium">{formatCurrency(aAfterDemo)} ({pAfterDemo}%)</span>
                            </div>
                            <div className="flex justify-between">
                              <span>{cfg.advance_payment_after_project_text || 'After Project Completion'}:</span>
                              <span className="font-medium">{formatCurrency(aAfterProject)} ({pAfterProject}%)</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}

              {/* Customer Information */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Customer Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Name</p>
                    <p className="text-lg text-gray-900 dark:text-white font-semibold">{selectedEMISplitPayment.user_name || 'N/A'}</p>
                  </div>
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Email</p>
                    <p className="text-lg text-gray-900 dark:text-white font-semibold">{selectedEMISplitPayment.user_email || 'N/A'}</p>
                  </div>
                  {selectedEMISplitPayment.user_phone && (
                    <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                      <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Phone</p>
                      <p className="text-lg text-gray-900 dark:text-white font-semibold">{selectedEMISplitPayment.user_phone}</p>
                    </div>
                  )}
                  {selectedEMISplitPayment.user_whatsapp && (
                    <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                      <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">WhatsApp</p>
                      <p className="text-lg text-gray-900 dark:text-white font-semibold">{selectedEMISplitPayment.user_whatsapp}</p>
                    </div>
                  )}
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Order Date</p>
                    <p className="text-lg text-gray-900 dark:text-white font-semibold">{formatDate(selectedEMISplitPayment.order_created_at || selectedEMISplitPayment.created_at)}</p>
                  </div>
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Payment Method</p>
                    <p className="text-lg text-gray-900 dark:text-white font-semibold">{mapPaymentMethodToDisplay(selectedEMISplitPayment.payment_method)}</p>
                  </div>
                </div>
              </div>
              {/* Order Items */}
              {selectedEMISplitPayment.items && selectedEMISplitPayment.items.length > 0 && (
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Order Items</h3>
                  <div className="space-y-6">
                    {selectedEMISplitPayment.items.map((item, index) => (
                      <div key={index} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-lg border border-white/20 p-8">
                        {/* Product Header */}
                        <div className="flex items-center space-x-4 mb-4">
                          {/* Product Image */}
                          {item.product_slug ? (
                            <Link 
                              to={`/product/${item.product_slug}`}
                              className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer"
                            >
                              {item.product_image ? (
                                <img 
                                  src={getImageUrl(item.product_image)} 
                                  alt={item.product_name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className={`w-full h-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center ${item.product_image ? 'hidden' : ''}`}>
                                <Package className="w-8 h-8 text-gray-400" />
                              </div>
                            </Link>
                          ) : (
                            <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                              {item.product_image ? (
                                <img 
                                  src={getImageUrl(item.product_image)} 
                                  alt={item.product_name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className={`w-full h-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center ${item.product_image ? 'hidden' : ''}`}>
                                <Package className="w-8 h-8 text-gray-400" />
                              </div>
                            </div>
                          )}
                          
                          {/* Product Info */}
                          <div className="flex-1">
                            {item.product_slug ? (
                              <Link 
                                to={`/product/${item.product_slug}`}
                                className="text-lg font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer"
                              >
                                {item.product_name}
                              </Link>
                            ) : (
                              <p className="text-lg font-medium text-gray-900 dark:text-white">{item.product_name}</p>
                            )}
                            <p className="text-sm text-gray-500 dark:text-gray-400">Quantity: {item.quantity}</p>
                          </div>
                        </div>

                        {/* Pricing Details */}
                        <div className="space-y-3">
                          {/* License Information */}
                          {item.selected_license && (
                            <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
                              <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                                {item.selected_license === 'extended' ? 'Extended License' : 'Regular License'}
                              </span>
                              <div className="text-right">
                                <span className="font-medium">
                                  {formatCurrency(item.selected_license === 'extended' ? 
                                    (item.license_details?.extended_license_price || item.extended_license_price || 0) :
                                    (item.license_details?.regular_license_price || item.regular_license_price || 0)
                                  )}
                                </span>
                                {/* Show original price if different */}
                                {item.selected_license === 'regular' && item.license_details?.regular_license_original_price && 
                                 item.license_details.regular_license_original_price !== item.license_details.regular_license_price && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    <span className="line-through">
                                      {formatCurrency(item.license_details.regular_license_original_price)}
                                    </span>
                                    {item.license_details.regular_license_discount > 0 && (
                                      <span className="ml-2 text-green-600 dark:text-green-400">{item.license_details.regular_license_discount}% OFF</span>
                                    )}
                                  </div>
                                )}
                                {item.selected_license === 'extended' && item.license_details?.extended_license_original_price && 
                                 item.license_details.extended_license_original_price !== item.license_details.extended_license_price && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    <span className="line-through">
                                      {formatCurrency(item.license_details.extended_license_original_price)}
                                    </span>
                                    {item.license_details.extended_license_discount > 0 && (
                                      <span className="ml-2 text-green-600 dark:text-green-400">{item.license_details.extended_license_discount}% OFF</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Extended Support Information */}
                          {item.extended_support && (
                            <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
                              <span className="text-sm font-medium text-green-600 dark:text-green-400">Extended Support</span>
                              <div className="text-right">
                                <span className="font-medium">
                                  {formatCurrency(item.support_details?.extended_support_price || item.extended_support_price || 0)}
                                </span>
                                {(item.support_details?.extended_support_original_price || item.extended_support_original_price) && 
                                 (item.support_details?.extended_support_original_price || item.extended_support_original_price) !== (item.support_details?.extended_support_price || item.extended_support_price) && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    <span className="line-through">
                                      {formatCurrency(item.support_details?.extended_support_original_price || item.extended_support_original_price)}
                                    </span>
                                    {(item.support_details?.extended_support_discount || item.extended_support_discount) > 0 && (
                                      <span className="ml-2 text-green-600 dark:text-green-400">{item.support_details?.extended_support_discount || item.extended_support_discount}% OFF</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Addons Information */}
                          {item.selected_addons && Array.isArray(item.selected_addons) && item.selected_addons.length > 0 && (
                            <div className="py-2 border-b border-gray-200 dark:border-gray-600">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-medium text-purple-600 dark:text-purple-400">Addons</span>
                              </div>
                              <div className="space-y-1">
                                {item.selected_addons.map((addonId, addonIndex) => {
                                  const addon = (item.addon_details || item.addons)?.find(a => a.id === addonId);
                                  return addon ? (
                                    <div key={addonIndex} className="flex justify-between items-center pl-4">
                                      <span className="text-sm text-gray-600 dark:text-gray-400">{addon.name}</span>
                                      <div className="text-right">
                                        <span className="text-sm font-medium">{formatCurrency(addon.price || 0)}</span>
                                        {addon.original_price && addon.original_price !== addon.price && (
                                          <div className="text-xs text-gray-500 dark:text-gray-400">
                                            <span className="line-through">{formatCurrency(addon.original_price)}</span>
                                            {addon.discount_percentage > 0 && (
                                              <span className="ml-1 text-green-600 dark:text-green-400">{addon.discount_percentage}% OFF</span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ) : null;
                                })}
                              </div>
                            </div>
                          )}

                          {/* Item Total */}
                          <div className="flex justify-between items-center pt-2">
                            <span className="text-lg font-semibold text-gray-900 dark:text-white">Total</span>
                            <span className="text-lg font-semibold text-gray-900 dark:text-white">
                              {(() => {
                                // Calculate total including support and addons
                                let itemTotal = (item.price || 0) * (item.quantity || 1);
                              
                                // Add support price if extended support is selected
                                if (item.extended_support && (item.support_details?.extended_support_price || item.extended_support_price)) {
                                  itemTotal += (item.support_details?.extended_support_price || item.extended_support_price || 0) * (item.quantity || 1);
                                }
                              
                                // Add addon prices if addons are selected
                                if (item.selected_addons && Array.isArray(item.selected_addons) && item.selected_addons.length > 0 && (item.addon_details || item.addons)) {
                                  item.selected_addons.forEach(addonId => {
                                    const addon = (item.addon_details || item.addons)?.find(a => a.id === addonId);
                                    if (addon && addon.price) {
                                      itemTotal += addon.price * (item.quantity || 1);
                                    }
                                  });
                                }
                              
                                return formatCurrency(itemTotal);
                              })()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Billing Address */}
              {selectedEMISplitPayment.billing_address && (
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Billing Address</h3>
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-lg border border-white/20 p-8">
                    <p className="text-gray-600 dark:text-gray-400">
                      {typeof selectedEMISplitPayment.billing_address === 'string' 
                        ? selectedEMISplitPayment.billing_address 
                        : JSON.stringify(selectedEMISplitPayment.billing_address, null, 2)
                      }
                    </p>
                  </div>
                </div>
              )}

              {/* Notes */}
              {(selectedEMISplitPayment.notes || selectedEMISplitPayment.order_notes) && (
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Notes</h3>
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-lg border border-white/20 p-8">
                    <p className="text-gray-600 dark:text-gray-400">
                      {selectedEMISplitPayment.notes || selectedEMISplitPayment.order_notes}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && selectedOrder.order_number && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[60] p-2 sm:p-4 md:p-6">
          <div className="card-3d bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl border border-white/30 w-full max-w-7xl h-[95vh] overflow-hidden flex flex-col">
            <div className="p-4 sm:p-6 md:p-8 border-b-2 border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
                <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                    <Package className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white truncate">
                      Order Details
                </h2>
                    <p className="text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-400 font-semibold truncate">
                      #{selectedOrder.order_number}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 sm:space-x-4 w-full sm:w-auto">
                  <button
                    onClick={() => handleDownloadOrderPDF(selectedOrder)}
                    className="card-3d bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-xl sm:rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow flex items-center gap-2 sm:gap-3 text-sm sm:text-base touch-manipulation"
                    title="Download Order PDF"
                  >
                    <Download className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span className="hidden sm:inline">Download PDF</span>
                    <span className="sm:hidden">PDF</span>
                  </button>
                  <button
                    onClick={() => handlePrintOrder(selectedOrder)}
                    className="card-3d bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-xl sm:rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow flex items-center gap-2 sm:gap-3 text-sm sm:text-base touch-manipulation"
                    title="Print Order"
                  >
                    <Printer className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span className="hidden sm:inline">Print Order</span>
                    <span className="sm:hidden">Print</span>
                  </button>
                  <button 
                    onClick={closeOrderModal}
                    className="p-2 sm:p-3 md:p-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-xl sm:rounded-2xl transition-all duration-300 touch-manipulation"
                  >
                    <X className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-8 space-y-8 overflow-y-auto flex-1">
              {/* Order Summary */}
              <div className="card-3d bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 backdrop-blur-md rounded-3xl shadow-lg border border-blue-200 dark:border-blue-800 p-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Activity className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Order Status</p>
                    <p className={`text-2xl font-bold ${getStatusColor(selectedOrder.order_status, 'order')}`}>
                    {mapOrderStatusToDisplay(selectedOrder.order_status)}
                  </p>
                </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <CreditCard className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Payment Status</p>
                    <p className={`text-2xl font-bold ${getStatusColor(selectedOrder.payment_status, 'payment')}`}>
                  {mapPaymentStatusToDisplay(selectedOrder.payment_status)}
                  </p>
                </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <DollarSign className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Total Amount</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(selectedOrder.total_amount || 0)}
                  </p>
                  </div>
                </div>
              </div>

              {/* Enhanced Order Breakdown - Always show when products are purchased */}
              {selectedOrder && selectedOrder.items && selectedOrder.items.length > 0 && (() => {
                // Calculate products total from all items
                const productsTotal = selectedOrder.items.reduce((total, item) => {
                  let itemTotal = (item.price || 0) * (item.quantity || 1);
                  
                  // Add support price if extended support is selected
                  if (item.extended_support && (item.support_details?.extended_support_price || item.extended_support_price)) {
                    itemTotal += (item.support_details?.extended_support_price || item.extended_support_price || 0) * (item.quantity || 1);
                  }
                  
                  // Add addon prices if addons are selected
                  if (item.selected_addons && Array.isArray(item.selected_addons) && item.selected_addons.length > 0 && (item.addon_details || item.addons)) {
                    item.selected_addons.forEach(addonId => {
                      const addon = (item.addon_details || item.addons)?.find(a => a.id === addonId);
                      if (addon && addon.price) {
                        itemTotal += addon.price * (item.quantity || 1);
                      }
                    });
                  }
                  
                  return total + itemTotal;
                }, 0);

                // Get coupon information
                const couponCode = selectedOrder.payment_option_details?.appliedCoupon?.code ||
                                 (selectedOrder.applied_coupon ? (() => {
                                   try {
                                     const parsed = typeof selectedOrder.applied_coupon === 'string' ? JSON.parse(selectedOrder.applied_coupon) : selectedOrder.applied_coupon;
                                     return parsed.code;
                                   } catch (e) {
                                     return null;
                                   }
                                 })() : null) ||
                                 selectedOrder.coupon_code ||
                                 null;
                
                const calculatedDiscountAmount = selectedOrder.payment_option_details?.appliedCoupon?.calculated_discount_amount ||
                                               (selectedOrder.applied_coupon ? (() => {
                                                 try {
                                                   const parsed = typeof selectedOrder.applied_coupon === 'string' ? JSON.parse(selectedOrder.applied_coupon) : selectedOrder.applied_coupon;
                                                   return parsed.calculated_discount_amount;
                                                 } catch (e) {
                                                   return 0;
                                                 }
                                               })() : 0) ||
                                               selectedOrder.discount_value ||
                                               selectedOrder.coupon_discount_amount ||
                                               0;

                const hasCoupon = couponCode && calculatedDiscountAmount > 0;
                const finalAmount = selectedOrder.total_amount || 0;
                
                return (
                  <div className={`card-3d backdrop-blur-md rounded-3xl shadow-lg border p-8 ${
                    hasCoupon 
                      ? 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800'
                      : 'bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800'
                  }`}>
                    <h4 className={`text-lg font-bold mb-6 ${
                      hasCoupon 
                        ? 'text-green-800 dark:text-green-200' 
                        : 'text-blue-800 dark:text-blue-200'
                    }`}>
                      Order Breakdown
                    </h4>
                    <div className="space-y-3">
                      {/* Products Total */}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 dark:text-gray-400">Products Total:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatCurrency(productsTotal)}
                        </span>
                      </div>

                      {/* Coupon Code Details - only show if coupon is applied */}
                      {hasCoupon && (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 dark:text-gray-400">Coupon Code:</span>
                            <span className="font-medium text-green-600 dark:text-green-400">
                              {couponCode}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 dark:text-gray-400">Discount Amount:</span>
                            <span className="font-medium text-green-600 dark:text-green-400">
                              -{formatCurrency(calculatedDiscountAmount)}
                            </span>
                          </div>
                        </>
                      )}

                      {/* Grand Total */}
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-semibold text-gray-900 dark:text-white">Grand Total:</span>
                          <span className="text-lg font-bold text-gray-900 dark:text-white">
                            {formatCurrency(finalAmount)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Split Payment Order Details */}
              {selectedOrder.payment_option_details?.is_split_payment_order && (
                <div className="card-3d bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 backdrop-blur-md rounded-3xl shadow-lg border border-orange-200 dark:border-orange-800 p-8">
                  <h4 className="text-lg font-bold text-orange-800 dark:text-orange-200 mb-6">Split Payment Order Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-orange-700 dark:text-orange-300">Split Payment Type</p>
                      <p className="text-lg font-semibold text-orange-900 dark:text-orange-100">
                        {selectedOrder.payment_option_details.split_payment_text || 'Split Payment'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-orange-700 dark:text-orange-300">Parent Order</p>
                      <p className="text-lg font-semibold text-orange-900 dark:text-orange-100">
                        #{selectedOrder.payment_option_details.original_order_number}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-orange-700 dark:text-orange-300">Original Total Amount</p>
                      <p className="text-lg font-semibold text-orange-900 dark:text-orange-100">
                        {formatCurrency((() => {
                          const pod = selectedOrder.payment_option_details || {};
                          

                          
                          // Always try to find the parent order's total amount first
                          let parentOrderTotal = 0;
                          
                          // Try to find the parent order by order number
                          const byOrder = (typeof orders !== 'undefined' && Array.isArray(orders))
                            ? orders.find(o => o.order_number === pod.original_order_number)
                            : null;
                          if (byOrder) {
                            parentOrderTotal = byOrder.original_total_amount || byOrder.total_amount || 0;
                          }
                          
                          // If we didn't find by order number, try by parent split payment ID
                          if (!parentOrderTotal && pod.parent_split_payment_id) {
                            const parentSplitPayment = emiSplitPayments.find(p => p.id === parseInt(pod.parent_split_payment_id));
                            if (parentSplitPayment) {
                              if (parentSplitPayment.original_total_amount) {
                                parentOrderTotal = parseFloat(parentSplitPayment.original_total_amount);
                              } else if (parentSplitPayment.order_id && orders.length > 0) {
                                const relatedOrder = orders.find(order => order.id === parentSplitPayment.order_id);
                                if (relatedOrder) {
                                  parentOrderTotal = relatedOrder.original_total_amount || relatedOrder.total_amount || 0;
                                }
                              } else {
                                parentOrderTotal = parseFloat(parentSplitPayment.order_total || parentSplitPayment.amount || 0);
                              }
                            }
                          }
                          
                          // If we still don't have a parent order total, use fallback
                          if (!parentOrderTotal) {
                            parentOrderTotal = selectedOrder.original_total_amount || selectedOrder.total_amount || 0;
                          }
                          return parentOrderTotal;
                        })())}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-orange-700 dark:text-orange-300">Previously Paid</p>
                      <p className="text-lg font-semibold text-orange-900 dark:text-orange-100">
                        {formatCurrency((() => {
                          const pod = selectedOrder.payment_option_details || {};
                          
                          // Get the parent split payment
                          const parentSplitPayment = pod.parent_split_payment_id ? 
                            emiSplitPayments.find(p => p.id === parseInt(pod.parent_split_payment_id)) : null;
                          
                          if (!parentSplitPayment) {
                            return selectedOrder.payment_option_details.paid_amount || 0;
                          }
                          
                          // Get the remaining amounts for the parent split payment
                          const remainingAmounts = getRemainingSplitPaymentAmounts(parentSplitPayment);
                          
                          if (!remainingAmounts) {
                            return selectedOrder.payment_option_details.paid_amount || 0;
                          }
                          
                          // Calculate previously paid: Advance Amount + Split Paid Amount (from parent split payment)
                          const advanceAmount = Number(remainingAmounts.advanceAmount || 0);
                          
                          // Calculate total split paid amount from parent split payment
                          let splitPaidAmount = 0;
                          
                          // Add demo amount if completed
                          if (remainingAmounts.afterDemoCompleted || 
                              remainingAmounts.afterDemoOrder?.payment_status === 'completed') {
                            splitPaidAmount += remainingAmounts.afterDemoAmount;
                          }
                          
                          // Add project amount if completed
                          if (remainingAmounts.afterProjectCompleted || 
                              remainingAmounts.afterProjectOrder?.payment_status === 'completed') {
                            splitPaidAmount += remainingAmounts.afterProjectAmount;
                          }
                          
                          const previouslyPaid = advanceAmount + splitPaidAmount;
                          

                          
                          return previouslyPaid;
                        })())}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-orange-700 dark:text-orange-300">This Payment Amount</p>
                      <p className="text-lg font-semibold text-orange-900 dark:text-orange-100">
                        {formatCurrency(selectedOrder.total_amount || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-orange-700 dark:text-orange-300">Remaining Amount</p>
                      <p className="text-lg font-semibold text-orange-900 dark:text-orange-100">
                        {formatCurrency((() => {
                          const pod = selectedOrder.payment_option_details || {};
                          
                          // Get the parent split payment
                          const parentSplitPayment = pod.parent_split_payment_id ? 
                            emiSplitPayments.find(p => p.id === parseInt(pod.parent_split_payment_id)) : null;
                          
                          if (!parentSplitPayment) {
                            return 0;
                          }
                          
                          // Get the remaining amounts for the parent split payment
                          const remainingAmounts = getRemainingSplitPaymentAmounts(parentSplitPayment);
                          
                          if (!remainingAmounts) {
                            return 0;
                          }
                          
                          // Calculate remaining amount based on completion status
                          let remainingTotal = 0;
                          
                          // Add demo amount if NOT completed
                          if (!remainingAmounts.afterDemoCompleted && 
                              remainingAmounts.afterDemoOrder?.payment_status !== 'completed') {
                            remainingTotal += remainingAmounts.afterDemoAmount;
                          }
                          
                          // Add project amount if NOT completed
                          if (!remainingAmounts.afterProjectCompleted && 
                              remainingAmounts.afterProjectOrder?.payment_status !== 'completed') {
                            remainingTotal += remainingAmounts.afterProjectAmount;
                          }
                          

                          
                          return remainingTotal;
                        })())}
                      </p>
                    </div>
                    {selectedOrder.payment_option_details.parent_split_payment_id && (
                      <div>
                        <p className="text-sm font-medium text-orange-700 dark:text-orange-300">Parent Split Payment ID</p>
                        <p className="text-lg font-semibold text-orange-900 dark:text-orange-100">
                          #{selectedOrder.payment_option_details.parent_split_payment_id}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Split Payment Order Breakdown with Coupon */}
              {selectedOrder.payment_option_details?.is_split_payment_order && (() => {
                const couponCode = selectedOrder.payment_option_details?.appliedCoupon?.code || 
                                 selectedOrder.payment_option_details?.coupon_code ||
                                 (selectedOrder.applied_coupon ? (() => {
                                   try {
                                     const parsed = typeof selectedOrder.applied_coupon === 'string' ? JSON.parse(selectedOrder.applied_coupon) : selectedOrder.applied_coupon;
                                     return parsed.code;
                                   } catch (e) {
                                     return null;
                                   }
                                 })() : null) ||
                                 null;
                
                const calculatedDiscountAmount = selectedOrder.payment_option_details?.appliedCoupon?.calculated_discount_amount ||
                                               (selectedOrder.applied_coupon ? (() => {
                                                 try {
                                                   const parsed = typeof selectedOrder.applied_coupon === 'string' ? JSON.parse(selectedOrder.applied_coupon) : selectedOrder.applied_coupon;
                                                   return parsed.calculated_discount_amount;
                                                 } catch (e) {
                                                   return 0;
                                                 }
                                               })() : 0) ||
                                               0;
                
                if (couponCode && calculatedDiscountAmount > 0) {
                  const originalAmount = Number(selectedOrder.total_amount || 0) + calculatedDiscountAmount;
                  
                  return (
                    <div className="card-3d bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 backdrop-blur-md rounded-3xl shadow-lg border border-green-200 dark:border-green-800 p-8">
                      <h4 className="text-lg font-bold text-green-800 dark:text-green-200 mb-6">Order Breakdown</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">Order Subtotal:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {formatCurrency(originalAmount)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">Coupon Code:</span>
                          <span className="font-medium text-green-600 dark:text-green-400">
                            {couponCode}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-400">Discount Amount:</span>
                          <span className="font-medium text-green-600 dark:text-green-400">
                            -{formatCurrency(calculatedDiscountAmount)}
                          </span>
                        </div>
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
                          <div className="flex justify-between items-center">
                            <span className="text-lg font-semibold text-gray-900 dark:text-white">Final Amount:</span>
                            <span className="text-lg font-bold text-gray-900 dark:text-white">
                              {formatCurrency(selectedOrder.total_amount || 0)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Payment Option Details */}
              {selectedOrder.payment_option && selectedOrder.payment_option !== 'full' && (
                <div className="card-3d bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 backdrop-blur-md rounded-3xl shadow-lg border border-blue-200 dark:border-blue-800 p-8">
                  <h4 className="text-lg font-bold text-blue-800 dark:text-blue-200 mb-6">Payment Option Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Payment Type</p>
                      <p className={`text-lg font-semibold ${
                        selectedOrder.payment_option === 'advance' 
                          ? 'text-blue-600 dark:text-blue-400' 
                          : selectedOrder.payment_option === 'emi'
                          ? 'text-purple-600 dark:text-purple-400'
                          : 'text-green-600 dark:text-green-400'
                      }`}>
                        {selectedOrder.payment_option === 'advance' ? 'Split Payment' : 
                         selectedOrder.payment_option === 'emi' ? 'EMI Payment' : 'Full Payment'}
                      </p>
                    </div>
                    {selectedOrder.original_total_amount && (
                      <div>
                        <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Original Total</p>
                        <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                          {formatCurrency(selectedOrder.original_total_amount)}
                        </p>
                      </div>
                    )}
                    {selectedOrder.advance_amount && (
                      <div>
                        <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Advance Amount</p>
                        <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                          {formatCurrency(selectedOrder.advance_amount)}
                        </p>
                      </div>
                    )}
                    {selectedOrder.remaining_amount && selectedOrder.remaining_amount > 0 && (
                      <div>
                        <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Remaining Amount</p>
                        <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                          {formatCurrency(selectedOrder.remaining_amount)}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Payment Option Instructions */}
                  {selectedOrder.payment_option === 'advance' && (
                    <div className="mt-3 p-3 bg-blue-100 dark:bg-blue-800/30 rounded-md">
                      <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
                        Your Split Payment plan details are shown below.
                      </p>
                      {(() => {
                        // Use current payment configuration instead of stored configuration
                        const cfg = currentPaymentConfig || selectedOrder.payment_option_details?.paymentOptionsConfig || {};
                        const totalAmount = parseFloat(selectedOrder.original_total_amount || selectedOrder.total_amount || 0) || 0;
                        const pNow = parseFloat(cfg.advance_payment_percentage || 50);
                        const pAfterDemo = parseFloat(cfg.advance_payment_after_demo_percentage || 25);
                        const pAfterProject = parseFloat(cfg.advance_payment_after_project_percentage || 25);
                        const aNow = (totalAmount * pNow) / 100;
                        const aAfterDemo = (totalAmount * pAfterDemo) / 100;
                        const aAfterProject = (totalAmount * pAfterProject) / 100;
                        return (
                          <div className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
                            <div className="flex justify-between">
                              <span>{cfg.advance_payment_now_text || 'Split (Now)'}:</span>
                              <span className="font-medium">{formatCurrency(aNow)} ({pNow}%)</span>
                            </div>
                            <div className="flex justify-between">
                              <span>{cfg.advance_payment_after_demo_text || 'After Demo Completion'}:</span>
                              <span className="font-medium">{formatCurrency(aAfterDemo)} ({pAfterDemo}%)</span>
                            </div>
                            <div className="flex justify-between">
                              <span>{cfg.advance_payment_after_project_text || 'After Project Completion'}:</span>
                              <span className="font-medium">{formatCurrency(aAfterProject)} ({pAfterProject}%)</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  
                  {selectedOrder.payment_option === 'emi' && (
                    <div className="mt-3 p-3 bg-purple-100 dark:bg-purple-800/30 rounded-md">
                      <p className="text-sm text-purple-800 dark:text-purple-200">
                        Your EMI payment plan details are shown below. Please ensure timely payments.
                      </p>
                      
                      {/* EMI Details */}
                      <div className="mt-3 space-y-2">
                        {selectedOrder.payment_option_details?.emi_details ? (
                        <div className="mt-3 space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-purple-700 dark:text-purple-300">EMI Tenure:</span>
                            <span className="text-purple-900 dark:text-purple-100 font-medium">
                              {selectedOrder.payment_option_details.emi_details.selectedEmiTenure} months
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-purple-700 dark:text-purple-300">Monthly EMI:</span>
                            <span className="text-purple-900 dark:text-purple-100 font-medium">
                              {formatCurrency(selectedOrder.payment_option_details.emi_details.emiAmount || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-purple-700 dark:text-purple-300">EMI Principal:</span>
                            <span className="text-purple-900 dark:text-purple-100 font-medium">
                              {formatCurrency(selectedOrder.payment_option_details.emi_details.emiPrincipal || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-purple-700 dark:text-purple-300">Processing Fee:</span>
                            <span className="text-purple-900 dark:text-purple-100 font-medium">
                              {formatCurrency(selectedOrder.payment_option_details?.paymentOptionsConfig?.emi_processing_fee || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-purple-700 dark:text-purple-300">Interest Rate:</span>
                            <span className="text-purple-900 dark:text-purple-100 font-medium">
                              {selectedOrder.payment_option_details?.paymentOptionsConfig?.emi_interest_rate || 0}% p.a.
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-purple-700 dark:text-purple-300">Total EMI Amount:</span>
                            <span className="text-purple-900 dark:text-purple-100 font-medium">
                              {formatCurrency(selectedOrder.payment_option_details.emi_details.totalEmiAmount || 0)}
                            </span>
                          </div>
                        </div>
                        ) : (
                          <div className="text-purple-700 dark:text-purple-300 text-sm">
                            EMI details not available
                          </div>
                        )}
                      </div>
                    
                    </div>
                  )}
                </div>
              )}

              {/* Customer Information */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Customer Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Name</p>
                    <p className="text-lg text-gray-900 dark:text-white font-semibold">{selectedOrder.customer_name}</p>
                  </div>
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Email</p>
                    <p className="text-lg text-gray-900 dark:text-white font-semibold">{selectedOrder.customer_email}</p>
                  </div>
                  {selectedOrder.customer_phone && (
                    <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                      <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Phone</p>
                      <p className="text-lg text-gray-900 dark:text-white font-semibold">{selectedOrder.customer_phone}</p>
                    </div>
                  )}
                  {selectedOrder.customer_whatsapp && (
                    <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                      <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">WhatsApp</p>
                      <p className="text-lg text-gray-900 dark:text-white font-semibold">{selectedOrder.customer_whatsapp}</p>
                    </div>
                  )}
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Order Date</p>
                    <p className="text-lg text-gray-900 dark:text-white font-semibold">{formatDate(selectedOrder.created_at)}</p>
                  </div>
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Payment Method</p>
                    <p className="text-lg text-gray-900 dark:text-white font-semibold">{mapPaymentMethodToDisplay(selectedOrder.payment_method)}</p>
                  </div>
                </div>
              </div>
              {/* Order Items */}
              {selectedOrder && selectedOrder.items && selectedOrder.items.length > 0 && (
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Order Items</h3>
                  <div className="space-y-6">
                    {selectedOrder.items.map((item, index) => (
                      <div key={index} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-lg border border-white/20 p-8">
                        {/* Product Header */}
                        <div className="flex items-center space-x-4 mb-4">
                          {/* Product Image */}
                          {item.product_slug ? (
                            <Link 
                              to={`/product/${item.product_slug}`}
                              className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer"
                            >
                              {item.product_image ? (
                                <img 
                                  src={getImageUrl(item.product_image)} 
                                  alt={item.product_name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className={`w-full h-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center ${item.product_image ? 'hidden' : ''}`}>
                                <Package className="w-8 h-8 text-gray-400" />
                              </div>
                            </Link>
                          ) : (
                            <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                              {item.product_image ? (
                                <img 
                                  src={getImageUrl(item.product_image)} 
                                  alt={item.product_name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className={`w-full h-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center ${item.product_image ? 'hidden' : ''}`}>
                                <Package className="w-8 h-8 text-gray-400" />
                              </div>
                            </div>
                          )}
                          
                          {/* Product Info */}
                          <div className="flex-1">
                            {item.product_slug ? (
                              <Link 
                                to={`/product/${item.product_slug}`}
                                className="text-lg font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer"
                              >
                                {item.product_name}
                              </Link>
                            ) : (
                              <p className="text-lg font-medium text-gray-900 dark:text-white">{item.product_name}</p>
                            )}
                            <p className="text-sm text-gray-500 dark:text-gray-400">Quantity: {item.quantity}</p>
                          </div>
                        </div>

                        {/* Pricing Details */}
                        <div className="space-y-3">
                          {/* License Information */}
                          {item.selected_license && (
                            <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
                              <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                                {item.selected_license === 'extended' ? 'Extended License' : 'Regular License'}
                              </span>
                        <div className="text-right">
                                <span className="font-medium">
                                  {formatCurrency(item.selected_license === 'extended' ? 
                                    (item.license_details?.extended_license_price || item.extended_license_price || 0) :
                                    (item.license_details?.regular_license_price || item.regular_license_price || 0)
                                  )}
                                </span>
                                {/* Show original price if different */}
                                {item.selected_license === 'regular' && item.license_details?.regular_license_original_price && 
                                 item.license_details.regular_license_original_price !== item.license_details.regular_license_price && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    <span className="line-through">
                                      {formatCurrency(item.license_details.regular_license_original_price)}
                                    </span>
                                    {item.license_details.regular_license_discount > 0 && (
                                      <span className="ml-2 text-green-600 dark:text-green-400">{item.license_details.regular_license_discount}% OFF</span>
                                    )}
                                  </div>
                                )}
                                {item.selected_license === 'extended' && item.license_details?.extended_license_original_price && 
                                 item.license_details.extended_license_original_price !== item.license_details.extended_license_price && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    <span className="line-through">
                                      {formatCurrency(item.license_details.extended_license_original_price)}
                                    </span>
                                    {item.license_details.extended_license_discount > 0 && (
                                      <span className="ml-2 text-green-600 dark:text-green-400">{item.license_details.extended_license_discount}% OFF</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Extended Support Information */}
                          {item.extended_support && (
                            <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
                              <span className="text-sm font-medium text-green-600 dark:text-green-400">Extended Support</span>
                              <div className="text-right">
                                <span className="font-medium">
                                  {formatCurrency(item.support_details?.extended_support_price || item.extended_support_price || 0)}
                                </span>
                                {(item.support_details?.extended_support_original_price || item.extended_support_original_price) && 
                                 (item.support_details?.extended_support_original_price || item.extended_support_original_price) !== (item.support_details?.extended_support_price || item.extended_support_price) && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    <span className="line-through">
                                      {formatCurrency(item.support_details?.extended_support_original_price || item.extended_support_original_price)}
                                    </span>
                                    {(item.support_details?.extended_support_discount || item.extended_support_discount) > 0 && (
                                      <span className="ml-2 text-green-600 dark:text-green-400">{item.support_details?.extended_support_discount || item.extended_support_discount}% OFF</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Addons Information */}
                          {item.selected_addons && Array.isArray(item.selected_addons) && item.selected_addons.length > 0 && (
                            <div className="py-2 border-b border-gray-200 dark:border-gray-600">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-medium text-purple-600 dark:text-purple-400">Addons</span>
                              </div>
                              <div className="space-y-1">
                                {item.selected_addons.map((addonId, addonIndex) => {
                                  const addon = (item.addon_details || item.addons)?.find(a => a.id === addonId);
                                  return addon ? (
                                    <div key={addonIndex} className="flex justify-between items-center pl-4">
                                      <span className="text-sm text-gray-600 dark:text-gray-400">{addon.name}</span>
                                      <div className="text-right">
                                        <span className="text-sm font-medium">{formatCurrency(addon.price || 0)}</span>
                                        {addon.original_price && addon.original_price !== addon.price && (
                                          <div className="text-xs text-gray-500 dark:text-gray-400">
                                            <span className="line-through">{formatCurrency(addon.original_price)}</span>
                                            {addon.discount_percentage > 0 && (
                                              <span className="ml-1 text-green-600 dark:text-green-400">{addon.discount_percentage}% OFF</span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ) : null;
                                })}
                              </div>
                            </div>
                          )}

                          {/* Item Total */}
                          <div className="flex justify-between items-center pt-2">
                            <span className="text-lg font-semibold text-gray-900 dark:text-white">Total</span>
                            <span className="text-lg font-semibold text-gray-900 dark:text-white">
                              {(() => {
                                // Calculate total including support and addons
                                let itemTotal = (item.price || 0) * (item.quantity || 1);
                                
                                // Add support price if extended support is selected
                                if (item.extended_support && (item.support_details?.extended_support_price || item.extended_support_price)) {
                                  itemTotal += (item.support_details?.extended_support_price || item.extended_support_price || 0) * (item.quantity || 1);
                                }
                                
                                // Add addon prices if addons are selected
                                if (item.selected_addons && Array.isArray(item.selected_addons) && item.selected_addons.length > 0 && (item.addon_details || item.addons)) {
                                  item.selected_addons.forEach(addonId => {
                                    const addon = (item.addon_details || item.addons)?.find(a => a.id === addonId);
                                    if (addon && addon.price) {
                                      itemTotal += addon.price * (item.quantity || 1);
                                    }
                                  });
                                }
                                
                                return formatCurrency(itemTotal);
                              })()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Rating and Review */}
              {(selectedOrder.rating || selectedOrder.review) && (
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                    <Star className="w-8 h-8 text-yellow-500" />
                    Your Rating & Review
                  </h3>
                  <div className="card-3d bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 backdrop-blur-md rounded-3xl shadow-lg border border-yellow-200 dark:border-yellow-800 p-8">
                    {selectedOrder.rating && (
                      <div className="flex items-center space-x-2 mb-3">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-6 h-6 ${
                              star <= selectedOrder.rating
                                ? 'text-yellow-500 fill-current'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                        <span className="ml-2 text-lg font-semibold text-yellow-900 dark:text-yellow-100">
                          {selectedOrder.rating}/5
                        </span>
                      </div>
                    )}
                    {selectedOrder.review && (
                      <div className="mb-3">
                        <p className="text-gray-700 dark:text-gray-300 italic">
                          "{selectedOrder.review}"
                        </p>
                      </div>
                    )}
                    {selectedOrder.review_date && (
                      <p className="text-xs text-yellow-700 dark:text-yellow-300">
                        Reviewed on: {new Date(selectedOrder.review_date).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Billing Address */}
              {selectedOrder.billing_address && (
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Billing Address</h3>
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-lg border border-white/20 p-8">
                    <p className="text-gray-600 dark:text-gray-400">
                      {typeof selectedOrder.billing_address === 'string' 
                        ? selectedOrder.billing_address 
                        : JSON.stringify(selectedOrder.billing_address, null, 2)
                      }
                    </p>
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedOrder.notes && (
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Notes</h3>
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-lg border border-white/20 p-8">
                    <p className="text-gray-600 dark:text-gray-400">{selectedOrder.notes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Project Creation Modal */}
      {showProjectModal && (
        <ProjectCreationModal
          isOpen={showProjectModal}
          onClose={closeProjectModal}
          onSubmit={handleProjectSubmit}
          loading={projectLoading}
          error={projectError}
        />
      )}

      {/* Quote Creation Modal hidden */}



      {/* Appointment Booking Modal */}
      {showAppointmentModal && (
        <AppointmentBookingModal
          isOpen={showAppointmentModal}
          onClose={closeAppointmentModal}
          onSubmit={handleAppointmentSubmit}
          loading={appointmentLoading}
          error={appointmentError}
        />
      )}



      {/* Invoice Details Modal */}
      {showInvoiceModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[60] p-6">
          <div className="card-3d bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/30 w-full max-w-7xl h-[95vh] overflow-hidden flex flex-col">
            <div className="p-8 border-b-2 border-gray-200 dark:border-gray-700 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/10 dark:to-emerald-900/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <Receipt className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold text-gray-900 dark:text-white">
                      Invoice Details
                </h2>
                    <p className="text-lg text-gray-600 dark:text-gray-400 font-semibold">
                      #{selectedInvoice.id}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => handleDownloadInvoicePDF(selectedInvoice)}
                    className="card-3d bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow flex items-center gap-3"
                    title="Download Invoice PDF"
                  >
                    <Download className="h-5 w-5" />
                    Download PDF
                  </button>
                  <button
                    onClick={() => handlePrintInvoice(selectedInvoice)}
                    className="card-3d bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow flex items-center gap-3"
                    title="Print Invoice"
                  >
                    <Printer className="h-5 w-5" />
                    Print Invoice
                  </button>
                  <button 
                    onClick={closeInvoiceModal}
                    className="p-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-2xl transition-all duration-300"
                  >
                    <X className="h-8 w-8" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-8 space-y-8 overflow-y-auto flex-1">
              {/* Invoice Summary */}
              <div className="card-3d bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 backdrop-blur-md rounded-3xl shadow-lg border border-green-200 dark:border-green-800 p-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <FileText className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Invoice Status</p>
                    <p className={`text-2xl font-bold ${getStatusColor(selectedInvoice.status)}`}>
                    {selectedInvoice.status || 'Unknown'}
                  </p>
                </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <CreditCard className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Payment Status</p>
                    <p className={`text-2xl font-bold ${getStatusColor(selectedInvoice.payment_status, 'payment')}`}>
                    {mapPaymentStatusToDisplay(selectedInvoice.payment_status)}
                  </p>
                </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <DollarSign className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Total Amount</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(selectedInvoice.amount || 0)}
                  </p>
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Customer Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Name</p>
                    <p className="text-lg text-gray-900 dark:text-white font-semibold">{selectedInvoice.customer_name}</p>
                  </div>
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Email</p>
                    <p className="text-lg text-gray-900 dark:text-white font-semibold">{selectedInvoice.customer_email}</p>
                  </div>
                  {selectedInvoice.customer_phone && (
                    <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                      <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Phone</p>
                      <p className="text-lg text-gray-900 dark:text-white font-semibold">{selectedInvoice.customer_phone}</p>
                    </div>
                  )}
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Invoice Date</p>
                    <p className="text-lg text-gray-900 dark:text-white font-semibold">{formatDate(selectedInvoice.created_at)}</p>
                  </div>
                </div>
              </div>

              {/* Invoice Items */}
              {selectedInvoice.items && selectedInvoice.items.length > 0 && (
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Invoice Items</h3>
                  <div className="space-y-4">
                    {selectedInvoice.items.map((item, index) => (
                      <div key={index} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 dark:text-white">{item.product_name}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Quantity: {item.quantity} × {formatCurrency(item.price || 0)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-gray-900 dark:text-white">
                            {formatCurrency(item.total || 0)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Billing Address */}
              {selectedInvoice.billing_address && (
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Billing Address</h3>
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-lg border border-white/20 p-8">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {typeof selectedInvoice.billing_address === 'string' 
                        ? selectedInvoice.billing_address 
                        : JSON.stringify(selectedInvoice.billing_address, null, 2)
                      }
                    </p>
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedInvoice.notes && (
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Notes</h3>
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-lg border border-white/20 p-8">
                    <p className="text-sm text-gray-600 dark:text-gray-400">{selectedInvoice.notes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quote Details Modal */}
      {showQuoteDetailsModal && selectedQuote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Quote Details - #{selectedQuote.id}
                </h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleDownloadQuotePDF(selectedQuote)}
                    className="p-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    title="Download Quote PDF"
                  >
                    <Download className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handlePrintQuote(selectedQuote)}
                    className="p-2 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                    title="Print Quote"
                  >
                    <Printer className="h-5 w-5" />
                  </button>
                  <button 
                    onClick={closeQuoteDetailsModal}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Quote Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Quote Status</p>
                  <p className={`text-lg font-semibold ${getStatusColor(selectedQuote.status)}`}>
                    {selectedQuote.status || 'Unknown'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Quote Amount</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(selectedQuote.amount || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Valid Until</p>
                  <p className="text-gray-900 dark:text-white">{formatDate(selectedQuote.valid_until)}</p>
                </div>
              </div>

              {/* Customer Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Customer Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Name</p>
                    <p className="text-gray-900 dark:text-white">{selectedQuote.client_name}</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Email</p>
                    <p className="text-gray-900 dark:text-white">{selectedQuote.client_email}</p>
                  </div>
                  {selectedQuote.client_phone && (
                    <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Phone</p>
                      <p className="text-gray-900 dark:text-white">{selectedQuote.client_phone}</p>
                    </div>
                  )}
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Project Name</p>
                    <p className="text-gray-900 dark:text-white">{selectedQuote.project_name}</p>
                  </div>
                </div>
              </div>

              {/* Description */}
              {selectedQuote.description && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Description</h3>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                    <p className="text-gray-600 dark:text-gray-400">{selectedQuote.description}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Appointment Details Modal */}
      {showAppointmentDetailsModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-start sm:items-center justify-center z-[60] p-4 sm:p-6">
          <div className="card-3d bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl border border-white/30 w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
            <div className="p-4 sm:p-6 md:p-8 border-b-2 border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-900/10 dark:to-violet-900/10">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 dark:text-white">
                      Appointment Details
                    </h2>
                    <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 font-semibold">
                      #{selectedAppointment.id}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:space-x-4 w-full sm:w-auto">
                  <button
                    onClick={() => handleDownloadAppointmentPDF(selectedAppointment)}
                    className="card-3d bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow flex items-center justify-center gap-2 sm:gap-3 w-full sm:w-auto"
                    title="Download Appointment PDF"
                  >
                    <Download className="h-5 w-5" />
                    <span className="hidden sm:inline">Download PDF</span>
                    <span className="sm:hidden">Download</span>
                  </button>
                  <button
                    onClick={() => handlePrintAppointment(selectedAppointment)}
                    className="card-3d bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow flex items-center justify-center gap-2 sm:gap-3 w-full sm:w-auto"
                    title="Print Appointment"
                  >
                    <Printer className="h-5 w-5" />
                    <span className="hidden sm:inline">Print Appointment</span>
                    <span className="sm:hidden">Print</span>
                  </button>
                  <button 
                    onClick={closeAppointmentDetailsModal}
                    className="p-2 sm:p-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-2xl transition-all duration-300 self-end sm:self-auto"
                  >
                    <X className="h-6 w-6 sm:h-8 sm:w-8" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8 overflow-y-auto flex-1">
              {/* Appointment Summary */}
              <div className="card-3d bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 backdrop-blur-md rounded-3xl shadow-lg border border-purple-200 dark:border-purple-800 p-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Activity className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Appointment Status</p>
                    <p className={`text-2xl font-bold ${getStatusColor(selectedAppointment.status)}`}>
                    {selectedAppointment.status || 'Unknown'}
                  </p>
                </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Calendar className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Preferred Date</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatDate(selectedAppointment.preferred_date)}
                  </p>
                </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <Clock className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Preferred Time</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedAppointment.preferred_time}
                  </p>
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Customer Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Name</p>
                    <p className="text-lg text-gray-900 dark:text-white font-semibold">{selectedAppointment.name}</p>
                  </div>
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Email</p>
                    <p className="text-lg text-gray-900 dark:text-white font-semibold">{selectedAppointment.email}</p>
                  </div>
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Phone</p>
                    <p className="text-lg text-gray-900 dark:text-white font-semibold">{selectedAppointment.phone}</p>
                  </div>
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Status</p>
                    <p className="text-lg text-gray-900 dark:text-white font-semibold">{selectedAppointment.status}</p>
                  </div>
                </div>
              </div>

              {/* Purpose */}
              {selectedAppointment.purpose && (
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Purpose</h3>
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-lg border border-white/20 p-8">
                    <p className="text-lg text-gray-600 dark:text-gray-400">{selectedAppointment.purpose}</p>
                  </div>
                </div>
              )}

              {/* Additional Message */}
              {selectedAppointment.message && (
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Additional Message</h3>
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-lg border border-white/20 p-8">
                    <p className="text-lg text-gray-600 dark:text-gray-400">{selectedAppointment.message}</p>
                  </div>
                </div>
              )}

              {/* Appointment Details */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Appointment Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                    <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Created At</p>
                    <p className="text-lg text-gray-900 dark:text-white font-semibold">{formatDate(selectedAppointment.created_at)}</p>
                  </div>
                  {selectedAppointment.updated_at && (
                    <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-6">
                      <p className="text-base font-bold text-gray-600 dark:text-gray-400 mb-2">Last Updated</p>
                      <p className="text-lg text-gray-900 dark:text-white font-semibold">{formatDate(selectedAppointment.updated_at)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <Footer />
      <FloatingChatSystem />
    </div>
  );
};
// Project Creation Modal Component
const ProjectCreationModal = ({ isOpen, onClose, onSubmit, loading = false, error = null }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    project_type: 'website',
    client_name: '',
    client_email: '',
    budget: '',
    requirements: '',
    live_url: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      project_type: 'website',
      client_name: '',
      client_email: '',
      budget: '',
      requirements: '',
      live_url: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="card-3d bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-3xl shadow-2xl border border-white/20 p-8 w-full max-w-2xl mx-4">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Create New Project</h2>
          <button
            onClick={handleClose}
            className="p-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-2xl transition-all duration-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-6 card-3d bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-red-200 dark:border-red-800 p-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center shadow-lg">
                <AlertCircle className="w-5 h-5 text-white" />
              </div>
              <p className="text-lg font-bold text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-lg font-bold text-gray-700 dark:text-gray-300 mb-3">
              Project Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full px-4 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white shadow-lg transition-all duration-300"
              placeholder="Enter project title"
            />
          </div>

          <div>
            <label className="block text-lg font-bold text-gray-700 dark:text-gray-300 mb-3">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white shadow-lg transition-all duration-300"
              placeholder="Describe your project"
            />
          </div>

          <div>
            <label className="block text-lg font-bold text-gray-700 dark:text-gray-300 mb-3">
              Project Type
            </label>
            <select
              name="project_type"
              value={formData.project_type}
              onChange={handleChange}
              className="w-full px-4 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white shadow-lg transition-all duration-300"
            >
              <option value="website">Website</option>
              <option value="mobile_app">Mobile App</option>
              <option value="web_app">Web App</option>
              <option value="ecommerce">E-commerce</option>
              <option value="marketing">Marketing</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-lg font-bold text-gray-700 dark:text-gray-300 mb-3">
                Client Name
              </label>
              <input
                type="text"
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
                className="w-full px-4 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white shadow-lg transition-all duration-300"
                placeholder="Client name"
              />
            </div>

            <div>
              <label className="block text-lg font-bold text-gray-700 dark:text-gray-300 mb-3">
                Client Email
              </label>
              <input
                type="email"
                name="client_email"
                value={formData.client_email}
                onChange={handleChange}
                className="w-full px-4 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white shadow-lg transition-all duration-300"
                placeholder="client@example.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-lg font-bold text-gray-700 dark:text-gray-300 mb-3">
              Budget (₹)
            </label>
            <input
              type="number"
              name="budget"
              value={formData.budget}
              onChange={handleChange}
              className="w-full px-4 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white shadow-lg transition-all duration-300"
              placeholder="50000"
            />
          </div>

          <div>
            <label className="block text-lg font-bold text-gray-700 dark:text-gray-300 mb-3">
              Requirements
            </label>
            <textarea
              name="requirements"
              value={formData.requirements}
              onChange={handleChange}
              rows="4"
              className="w-full px-4 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white shadow-lg transition-all duration-300"
              placeholder="Describe your project requirements"
            />
          </div>

          <div>
            <label className="block text-lg font-bold text-gray-700 dark:text-gray-300 mb-3">
              Live URL (Optional)
            </label>
            <input
              type="url"
              name="live_url"
              value={formData.live_url}
              onChange={handleChange}
              className="w-full px-4 py-4 border-2 border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white shadow-lg transition-all duration-300"
              placeholder="https://example.com"
            />
          </div>

          <div className="flex justify-end space-x-4 pt-6">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-3 text-base font-bold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-2xl transition-all duration-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-5 w-5" />
                  Create Project
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Quote Creation Modal Component
const QuoteCreationModal = ({ isOpen, onClose, onSubmit, loading = false, error = null, projects = [] }) => {
  const [formData, setFormData] = useState({
    projectType: 'website',
    features: [],
    timeline: 'standard',
    complexity: 'medium',
    contact: {
      name: '',
      email: ''
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleFeatureChange = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  const handleClose = () => {
    setFormData({
      projectType: 'website',
      features: [],
      timeline: 'standard',
      complexity: 'medium',
      contact: {
        name: '',
        email: ''
      }
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="card-3d bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-3xl shadow-2xl border border-white/20 p-8 w-full max-w-2xl mx-4">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Create New Quote</h2>
          <button
            onClick={handleClose}
            className="p-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-2xl transition-all duration-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-6 card-3d bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 backdrop-blur-md rounded-2xl shadow-lg border border-red-200 dark:border-red-800 p-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center shadow-lg">
                <AlertCircle className="w-5 h-5 text-white" />
              </div>
              <p className="text-lg font-bold text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Project Type
            </label>
            <select
              name="projectType"
              value={formData.projectType}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="website">Website</option>
              <option value="webapp">Web Application</option>
              <option value="mobile">Mobile App</option>
              <option value="ecommerce">E-commerce</option>
              <option value="design">Design</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Timeline
            </label>
            <select
              name="timeline"
              value={formData.timeline}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="rush">Rush (1.5x cost)</option>
              <option value="standard">Standard</option>
              <option value="extended">Extended (0.9x cost)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Complexity
            </label>
            <select
              name="complexity"
              value={formData.complexity}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="simple">Simple</option>
              <option value="medium">Medium</option>
              <option value="complex">Complex</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Features
            </label>
            <div className="space-y-2">
              {formData.projectType === 'website' && (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.features.includes('cms')}
                      onChange={() => handleFeatureChange('cms')}
                      className="mr-2"
                    />
                    CMS Integration
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.features.includes('seo')}
                      onChange={() => handleFeatureChange('seo')}
                      className="mr-2"
                    />
                    SEO Optimization
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.features.includes('analytics')}
                      onChange={() => handleFeatureChange('analytics')}
                      className="mr-2"
                    />
                    Analytics Integration
                  </label>
                </>
              )}
              {formData.projectType === 'webapp' && (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.features.includes('auth')}
                      onChange={() => handleFeatureChange('auth')}
                      className="mr-2"
                    />
                    Authentication System
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.features.includes('dashboard')}
                      onChange={() => handleFeatureChange('dashboard')}
                      className="mr-2"
                    />
                    Admin Dashboard
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.features.includes('api')}
                      onChange={() => handleFeatureChange('api')}
                      className="mr-2"
                    />
                    API Integration
                  </label>
                </>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Contact Name
            </label>
            <input
              type="text"
              name="contact.name"
              value={formData.contact.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Your name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Contact Email
            </label>
            <input
              type="email"
              name="contact.email"
              value={formData.contact.email}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="your@email.com"
              required
            />
          </div>



          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Quote'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Appointment Booking Modal Component
const AppointmentBookingModal = ({ isOpen, onClose, onSubmit, loading = false, error = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    purpose: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleClose = () => {
    setFormData({
      name: '',
      phone: '',
      preferredDate: '',
      preferredTime: '',
      purpose: '',
      message: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Book New Appointment</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg">
            <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Your full name"
            />
          </div>

          {/* Email field removed - will use authenticated user's email */}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Phone
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="+91 9876543210"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Preferred Date
            </label>
            <input
              type="date"
              name="preferredDate"
              value={formData.preferredDate}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Preferred Time
            </label>
            <input
              type="time"
              name="preferredTime"
              value={formData.preferredTime}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Purpose
            </label>
            <input
              type="text"
              name="purpose"
              value={formData.purpose}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Meeting purpose (e.g., Project Discussion, Consultation)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Additional Message
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Any additional details or special requirements"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Booking...' : 'Book Appointment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserDashboard; 